import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RectangleIcon from '@mui/icons-material/Rectangle';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import PanToolIcon from '@mui/icons-material/PanTool';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance from '../../services/axiosInstance';
import {
  IPdfTemplate,
  IPdfElement,
  IPdfElementStyle,
  PdfElementKind,
  A4_WIDTH_MM,
  A4_HEIGHT_MM,
  DEFAULT_PDF_TEMPLATE,
  DEFAULT_PAGE_SETTINGS,
  TemplateMode,
  LayoutPreset,
  IMultiProductLayout,
  IProductSlot,
  LAYOUT_PRESETS,
  ELEMENT_PRESETS,
  ELEMENT_KIND_LABELS,
  FONT_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  TEXT_TRANSFORM_OPTIONS,
  BORDER_STYLE_OPTIONS,
  BULLET_STYLE_OPTIONS,
  PRESET_TEMPLATES,
  FONTANA_COLORS,
} from '../../types/pdfTemplateTypes';
import {
  getPdfTemplates,
  createPdfTemplate,
  updatePdfTemplate,
  deletePdfTemplate,
  getPdfPreviewUrl,
  getMultiPdfPreviewUrl,
  getPdfTemplateThumbnailUrl,
  getMultiPdfTemplateThumbnailUrl,
  getPdfTemplateServiceErrorMessage,
} from '../../services/pdfTemplateService';
import { getPdfBlocks } from '../../services/pdfBlockService';
import { IPdfBlockTemplate } from '../../types/pdfBlockTypes';
import { ITemplate, IAsset } from '../../types/damTypes';
import {
  buildAvailableFields,
  buildSlotsFromLayout as buildSlotsFromLayoutUtil,
  getAssignedMultiSlotAsset,
  getAssetMetadataValue as getAssetMetadataValueUtil,
  getContentBoxMm as getContentBoxMmUtil,
  getMultiPageValidationIssues,
  getTemplatePreviewKey,
  normalizeTemplate as normalizeTemplateUtil,
  renderRichTextHtml as renderRichTextHtmlUtil,
  serializeTemplate as serializeTemplateUtil,
} from '../../features/pdfDesigner/pdfDesignerUtils';
import PdfSettingsDialogs, { PdfSettingsDialog } from '../../features/pdfDesigner/PdfSettingsDialogs';
import PdfPanelRail, { PdfPanelId } from '../../features/pdfDesigner/PdfPanelRail';
import PdfToolRail from '../../features/pdfDesigner/PdfToolRail';
import { getLayerOrderedElements, moveElementLayer, LayerMoveAction } from '../../features/pdfDesigner/pdfDesignerLayerUtils';

// MM to PX conversion at different zoom levels (96 DPI)
const MM_TO_PX_BASE = 3.7795275591;

// PT to PX conversion (96 DPI / 72 pt per inch)
const PT_TO_PX = 96 / 72; // ≈ 1.333

// Element icons
const ELEMENT_ICONS: Record<PdfElementKind, React.ReactNode> = {
  logo: <BrandingWatermarkIcon fontSize="small" />,
  productImage: <ImageIcon fontSize="small" />,
  textField: <DataObjectIcon fontSize="small" />,
  staticText: <TextFieldsIcon fontSize="small" />,
  richText: <PreviewIcon fontSize="small" />,
  rectangle: <RectangleIcon fontSize="small" />,
  line: <HorizontalRuleIcon fontSize="small" />,
  staticImage: <PhotoLibraryIcon fontSize="small" />,
  bulletList: <FormatListBulletedIcon fontSize="small" />,
};

const PANEL_SX = {
  borderRadius: 3,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
};

const TOOLBAR_SURFACE_SX = {
  p: 1.25,
  borderRadius: 2.5,
  border: '1px solid rgba(27, 57, 103, 0.08)',
  background: 'rgba(255,255,255,0.9)',
  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
};

const SEGMENTED_TOGGLE_SX = {
  p: 0.3,
  borderRadius: 2,
  backgroundColor: 'rgba(27, 57, 103, 0.06)',
  '& .MuiToggleButton-root': {
    border: 0,
    textTransform: 'none',
    color: '#35557f',
    borderRadius: '10px !important',
    px: 1.4,
    py: 0.7,
    fontWeight: 600,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: '#ffffff',
    color: FONTANA_COLORS.darkBlue,
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
  },
  '& .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: '#ffffff',
  },
};

const SEGMENTED_TABS_SX = {
  minHeight: 42,
  p: 0.4,
  borderRadius: 2,
  backgroundColor: 'rgba(27, 57, 103, 0.06)',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    minHeight: 34,
    textTransform: 'none',
    color: '#35557f',
    fontWeight: 600,
    borderRadius: 1.5,
    mx: 0.25,
  },
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: '#ffffff',
    color: FONTANA_COLORS.darkBlue,
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
  },
};

const SIDEBAR_ITEM_SX = {
  mb: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(27, 57, 103, 0.08)',
  backgroundColor: 'rgba(255,255,255,0.7)',
  '&.Mui-selected': {
    backgroundColor: 'rgba(27, 57, 103, 0.1)',
    borderColor: 'rgba(27, 57, 103, 0.22)',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(27, 57, 103, 0.14)',
  },
};

const PANEL_ACTION_BUTTON_SX = {
  justifyContent: 'flex-start',
  textTransform: 'none',
  borderRadius: 2,
  px: 1.4,
  py: 1,
  minHeight: 46,
  fontWeight: 700,
  letterSpacing: 0,
  color: FONTANA_COLORS.darkBlue,
  borderColor: 'rgba(27, 57, 103, 0.18)',
  backgroundColor: 'rgba(255,255,255,0.82)',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  '& .MuiButton-startIcon': {
    color: FONTANA_COLORS.darkBlue,
  },
  '&:hover': {
    borderColor: 'rgba(27, 57, 103, 0.34)',
    backgroundColor: 'rgba(27, 57, 103, 0.06)',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(15, 23, 42, 0.04)',
    borderColor: 'rgba(15, 23, 42, 0.08)',
  },
};

const PANEL_TILE_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  textAlign: 'center' as const,
  textTransform: 'none' as const,
  width: '100%',
  minHeight: 84,
  px: 1,
  py: 1.25,
  borderRadius: 2.5,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: 0,
  lineHeight: 1.2,
  color: FONTANA_COLORS.darkBlue,
  border: '1px solid rgba(27, 57, 103, 0.14)',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
  '& .panel-tile-icon': {
    width: 32,
    height: 32,
    borderRadius: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(27, 57, 103, 0.08)',
    color: FONTANA_COLORS.darkBlue,
    '& .MuiSvgIcon-root': { fontSize: 20 },
  },
  '&:hover': {
    borderColor: 'rgba(27, 57, 103, 0.34)',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.10)',
    transform: 'translateY(-1px)',
    backgroundColor: '#ffffff',
  },
  '&.Mui-disabled': {
    color: 'rgba(15, 23, 42, 0.34)',
    backgroundColor: 'rgba(15, 23, 42, 0.03)',
    borderColor: 'rgba(15, 23, 42, 0.08)',
    boxShadow: 'none',
    '& .panel-tile-icon': {
      backgroundColor: 'rgba(15, 23, 42, 0.05)',
      color: 'rgba(15, 23, 42, 0.34)',
    },
  },
};

const PANEL_LIST_BUTTON_SX = {
  justifyContent: 'flex-start',
  textTransform: 'none' as const,
  textAlign: 'left' as const,
  width: '100%',
  px: 1.25,
  py: 1,
  gap: 1,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: 13,
  letterSpacing: 0,
  color: FONTANA_COLORS.darkBlue,
  border: '1px solid rgba(27, 57, 103, 0.14)',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
  '& .panel-list-icon': {
    width: 30,
    height: 30,
    borderRadius: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(27, 57, 103, 0.08)',
    color: FONTANA_COLORS.darkBlue,
    flexShrink: 0,
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  '&:hover': {
    borderColor: 'rgba(27, 57, 103, 0.34)',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
    transform: 'translateY(-1px)',
    backgroundColor: '#ffffff',
  },
  '&.Mui-disabled': {
    color: 'rgba(15, 23, 42, 0.34)',
    backgroundColor: 'rgba(15, 23, 42, 0.03)',
    borderColor: 'rgba(15, 23, 42, 0.08)',
    boxShadow: 'none',
    '& .panel-list-icon': {
      backgroundColor: 'rgba(15, 23, 42, 0.05)',
      color: 'rgba(15, 23, 42, 0.34)',
    },
  },
};

const DEFAULT_PREVIEW_ARTIKELNUMMER = '97034';

const COLOR_SWATCH_BUTTON_SX = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '2px solid rgba(255,255,255,0.9)',
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
  cursor: 'pointer',
};

// Generate unique ID
const generateId = () => `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateSlotId = () => `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getContentBoxMm = (page: IPdfTemplate['page']) => {
  return getContentBoxMmUtil(page);
};

const buildSlotsFromLayout = (page: IPdfTemplate['page'], layout: IMultiProductLayout): IProductSlot[] => {
  return buildSlotsFromLayoutUtil(page, layout).map((slot) => ({ ...slot, id: generateSlotId() }));
};

const TemplateMiniPreview: React.FC<{ template: IPdfTemplate; selected: boolean }> = ({ template, selected }) => {
  const isLandscape = template.page.orientation === 'landscape';
  const pageWidthMm = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
  const pageHeightMm = isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM;
  const contentBox = getContentBoxMm(template.page);
  const previewElements = [...(template.elements || [])]
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    .slice(0, 10);

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: `${pageWidthMm} / ${pageHeightMm}`,
        minHeight: 92,
        borderRadius: 1.5,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: template.page.backgroundColor || '#fff',
        border: selected ? `2px solid ${FONTANA_COLORS.darkBlue}` : '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: selected ? '0 8px 20px rgba(27, 57, 103, 0.16)' : 'inset 0 0 0 1px rgba(255,255,255,0.7)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(27,57,103,0.04), rgba(74,123,167,0.08))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: `${(contentBox.xMm / pageWidthMm) * 100}%`,
          top: `${(contentBox.yMm / pageHeightMm) * 100}%`,
          width: `${(contentBox.widthMm / pageWidthMm) * 100}%`,
          height: `${(contentBox.heightMm / pageHeightMm) * 100}%`,
          border: '1px dashed rgba(27, 57, 103, 0.18)',
          borderRadius: 0.75,
        }}
      />
      {previewElements.map((element) => {
        const left = ((contentBox.xMm + element.xMm) / pageWidthMm) * 100;
        const top = ((contentBox.yMm + element.yMm) / pageHeightMm) * 100;
        const width = (element.wMm / pageWidthMm) * 100;
        const height = (element.hMm / pageHeightMm) * 100;
        const isText = element.kind === 'staticText' || element.kind === 'textField' || element.kind === 'richText' || element.kind === 'bulletList';
        const bgColor = element.kind === 'productImage' || element.kind === 'staticImage'
          ? 'rgba(74, 123, 167, 0.28)'
          : element.kind === 'rectangle'
            ? (element.style.backgroundColor || 'rgba(27, 57, 103, 0.18)')
            : isText
              ? 'rgba(27, 57, 103, 0.22)'
              : 'rgba(27, 57, 103, 0.32)';

        return (
          <Box
            key={element.id}
            sx={{
              position: 'absolute',
              left: `${left}%`,
              top: `${top}%`,
              width: `${Math.max(2, width)}%`,
              height: `${Math.max(1.5, height)}%`,
              borderRadius: element.kind === 'line' ? 0 : 0.6,
              backgroundColor: bgColor,
              border: element.kind === 'line' ? 0 : '1px solid rgba(27, 57, 103, 0.12)',
              borderTop: element.kind === 'line' ? '2px solid rgba(27, 57, 103, 0.45)' : undefined,
            }}
          />
        );
      })}
    </Box>
  );
};

const RenderedTemplateThumbnail: React.FC<{
  template: IPdfTemplate;
  selected: boolean;
  assetId?: string;
  assetIds?: string[];
  refreshKey: string;
}> = ({ template, selected, assetId, assetIds = [], refreshKey }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const isMulti = template.templateMode === 'multi';
  const canRender = isMulti ? assetIds.length > 0 : Boolean(assetId);
  const isLandscape = template.page.orientation === 'landscape';
  const pageWidthMm = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
  const pageHeightMm = isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM;

  useEffect(() => {
    let cancelled = false;

    if (!canRender) {
      setThumbnailUrl(null);
      setLoading(false);
      setFailed(false);
      if (objectUrlRef.current) {
        window.URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setFailed(false);

    const timer = window.setTimeout(async () => {
      try {
        const serializedTemplate = serializeTemplate(template);
        const nextUrl = isMulti
          ? await getMultiPdfTemplateThumbnailUrl(serializedTemplate, assetIds.slice(0, Math.max(1, template.maxProducts || assetIds.length)), 420)
          : await getPdfTemplateThumbnailUrl(serializedTemplate, assetId, 420);

        if (cancelled) {
          window.URL.revokeObjectURL(nextUrl);
          return;
        }

        if (objectUrlRef.current) {
          window.URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = nextUrl;
        setThumbnailUrl(nextUrl);
      } catch (error) {
        if (!cancelled) {
          console.warn('[PdfTemplatesPage] Could not render template thumbnail:', error);
          setFailed(true);
          setThumbnailUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [assetId, assetIds, canRender, isMulti, refreshKey, template]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        window.URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: `${pageWidthMm} / ${pageHeightMm}`,
        minHeight: 92,
        borderRadius: 1.5,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: template.page.backgroundColor || '#fff',
        border: selected ? `2px solid ${FONTANA_COLORS.darkBlue}` : '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: selected ? '0 8px 20px rgba(27, 57, 103, 0.16)' : 'inset 0 0 0 1px rgba(255,255,255,0.7)',
      }}
    >
      {thumbnailUrl ? (
        <Box
          component="img"
          src={thumbnailUrl}
          alt={`Förhandsvisning av ${template.name || 'mall'}`}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
          }}
        />
      ) : (
        <TemplateMiniPreview template={template} selected={selected} />
      )}
      {(loading || failed || !canRender) && (
        <Box
          sx={{
            position: 'absolute',
            left: 6,
            right: 6,
            bottom: 6,
            px: 0.75,
            py: 0.4,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'rgba(255,255,255,0.86)',
            color: 'text.secondary',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
          }}
        >
          {loading ? <CircularProgress size={12} /> : <ImageIcon sx={{ fontSize: 13 }} />}
          <Typography variant="caption" noWrap sx={{ fontSize: 10, fontWeight: 700 }}>
            {loading ? 'Renderar thumbnail' : failed ? 'Visar förenklad preview' : 'Välj previewprodukt'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Auto-fit text to container by shrinking font size when needed
const AutoFitText: React.FC<{
  text: string;
  enabled?: boolean;
  minFontSizePt?: number;
  depsKey?: string;
}> = ({ text, enabled, minFontSizePt, depsKey }) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (!el) return;
    if (!enabled) {
      el.style.fontSize = '';
      return;
    }
    const parent = el.parentElement as HTMLElement | null;
    if (!parent) return;
    const minPx = (minFontSizePt || 6) * PT_TO_PX;
    // Reset to inherited size before fitting
    el.style.fontSize = '';
    let fontSize = parseFloat(window.getComputedStyle(el).fontSize || '12');
    let guard = 200;
    while (guard-- > 0 && fontSize > minPx &&
      (el.scrollWidth > parent.clientWidth || el.scrollHeight > parent.clientHeight)) {
      fontSize -= 0.5;
      el.style.fontSize = `${fontSize}px`;
    }
  }, [text, enabled, minFontSizePt, depsKey]);

  return (
    <span
      ref={spanRef}
      style={{
        fontSize: 'inherit',
        fontWeight: 'inherit',
        fontStyle: 'inherit',
        fontFamily: 'inherit',
        color: 'inherit',
        width: '100%',
        display: 'block',
        textAlign: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      }}
    >
      {text}
    </span>
  );
};

// Helper to ensure template has all required properties with defaults
const normalizeTemplate = (
  template: Partial<IPdfTemplate>,
  coordinateSpace: 'workspace' | 'persisted' = 'workspace'
): IPdfTemplate => {
  return normalizeTemplateUtil(template, DEFAULT_PDF_TEMPLATE, DEFAULT_PAGE_SETTINGS, coordinateSpace);
};

const serializeTemplate = (template: IPdfTemplate): IPdfTemplate => {
  return serializeTemplateUtil(template);
};

// Helper to get metadata value from asset (mirrors backend logic)
const getAssetMetadataValue = (asset: IAsset | null, key: string, fallback?: string): string => {
  return getAssetMetadataValueUtil(asset, key, fallback || `{${key}}`);
};

type PreviewSearchCategory = {
  _id: string;
  name: string;
  parentCategory?: string | { _id?: string } | null;
};

const PREVIEW_PRIORITY_CATEGORY_NAMES = new Set(['produkter', 'products', 'produkt', 'product', 'vin', 'wine']);
const ARTICLE_METADATA_KEYS = ['artikelnummer', 'Artikelnummer', 'articleNumber', 'ArticleNumber', 'artnr', 'ArtNr', 'artnummer'];

const normalizePreviewSearchValue = (value: unknown): string => String(value || '').trim().toLowerCase();

const getPreviewCategoryParentId = (category: PreviewSearchCategory): string | null => {
  const parent = category.parentCategory;
  if (!parent) return null;
  if (typeof parent === 'string') return parent;
  return parent._id ? String(parent._id) : null;
};

const buildPreviewPriorityCategoryIds = (categories: PreviewSearchCategory[]): Set<string> => {
  const byParent = new Map<string, string[]>();
  const rootIds = categories
    .filter((category) => PREVIEW_PRIORITY_CATEGORY_NAMES.has(normalizePreviewSearchValue(category.name)))
    .map((category) => String(category._id));

  categories.forEach((category) => {
    const parentId = getPreviewCategoryParentId(category);
    if (!parentId) return;
    const children = byParent.get(parentId) || [];
    children.push(String(category._id));
    byParent.set(parentId, children);
  });

  const priorityIds = new Set<string>();
  const stack = [...rootIds];
  while (stack.length > 0) {
    const id = stack.pop();
    if (!id || priorityIds.has(id)) continue;
    priorityIds.add(id);
    stack.push(...(byParent.get(id) || []));
  }

  return priorityIds;
};

const getRawAssetMetadataValue = (asset: IAsset, key: string): string => {
  const metadata = asset.metadata as any;
  if (!metadata) return '';
  if (metadata instanceof Map) return String(metadata.get(key) || '').trim();
  return String(metadata[key] || '').trim();
};

const getAssetArticleNumbers = (asset: IAsset): string[] => {
  return ARTICLE_METADATA_KEYS
    .map((key) => getRawAssetMetadataValue(asset, key))
    .filter(Boolean);
};

const assetHasPriorityCategory = (asset: IAsset, priorityCategoryIds: Set<string>): boolean => {
  if (priorityCategoryIds.size === 0) return false;
  return (asset.categories || []).some((categoryId) => priorityCategoryIds.has(String(categoryId)));
};

const sortPreviewAssetOptions = (
  assets: IAsset[],
  search: string,
  priorityCategoryIds: Set<string>
): IAsset[] => {
  const term = normalizePreviewSearchValue(search);
  const seen = new Set<string>();

  return assets
    .filter((asset) => {
      if (!asset?._id || seen.has(asset._id)) return false;
      seen.add(asset._id);
      return true;
    })
    .map((asset, index) => {
      const articleNumbers = getAssetArticleNumbers(asset).map(normalizePreviewSearchValue);
      const title = normalizePreviewSearchValue(asset.title);
      const fileName = normalizePreviewSearchValue(asset.fileName);
      let score = 0;

      if (assetHasPriorityCategory(asset, priorityCategoryIds)) score += 2000;
      if ((asset as any).searchPriorityMatched) score += 500;
      if (term) {
        if (articleNumbers.some((value) => value === term)) score += 5000;
        else if (articleNumbers.some((value) => value.includes(term))) score += 3500;
        if (title.includes(term)) score += 900;
        if (fileName.includes(term)) score += 700;
      }

      return { asset, index, score };
    })
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ asset }) => asset)
    .slice(0, 50);
};

const escapeHtml = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const renderRichTextHtml = (raw: string, asset: IAsset | null): string => {
  return renderRichTextHtmlUtil(raw, asset);
};

const RICH_TEXT_FONTS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: "'Kapra Neue', 'Oswald', sans-serif", label: 'Kapra Neue' },
  { value: "'Oswald', sans-serif", label: 'Oswald' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
  { value: "'Georgia', serif", label: 'Georgia' },
];

const RICH_TEXT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

const RichTextWysiwygEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  availableFields: Array<{ id: string; label: string; templateName: string }>;
  baseStyle: IPdfElementStyle;
}> = ({ value, onChange, availableFields, baseStyle }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>('__UNINITIALIZED__');
  const selectionRef = useRef<Range | null>(null);
  const [insertField, setInsertField] = useState<string>('');
  const [selectedFont, setSelectedFont] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('#000000');

  // Initialize editor content on mount and keep updated when value changes externally
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = value || '';
    
    // Always initialize on first render (when lastValueRef is uninitialized)
    const isFirstRender = lastValueRef.current === '__UNINITIALIZED__';
    
    // Don't update while user is actively editing (unless it's initialization)
    if (!isFirstRender && document.activeElement === el) return;
    
    // Don't update if value hasn't changed (unless it's initialization)
    if (!isFirstRender && lastValueRef.current === next) return;
    
    el.innerHTML = next;
    lastValueRef.current = next;
  }, [value]);
  
  // Also ensure editor is populated when it first renders
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = value || '';
    if (el.innerHTML !== next) {
      el.innerHTML = next;
      lastValueRef.current = next;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const syncOut = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML || '';
    lastValueRef.current = html;
    onChange(html);
  }, [onChange]);

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    selectionRef.current = range.cloneRange();
  }, []);

  const restoreSelection = () => {
    const range = selectionRef.current;
    const sel = window.getSelection();
    const el = editorRef.current;
    if (!el || !sel || !range) return;
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const focusEditor = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    restoreSelection();
  };

  const exec = (command: string, val?: string) => {
    focusEditor();
    try {
      document.execCommand(command, false, val);
    } catch {}
    syncOut();
  };

  const insertHtml = (html: string) => {
    focusEditor();
    try {
      document.execCommand('insertHTML', false, html);
    } catch {
      const el = editorRef.current;
      if (el) el.innerHTML = (el.innerHTML || '') + html;
    }
    syncOut();
  };

  const insertText = (text: string) => {
    focusEditor();
    let ok = false;
    try {
      ok = document.execCommand('insertText', false, text);
    } catch {}
    if (!ok) {
      insertHtml(escapeHtml(text));
      return;
    }
    syncOut();
  };

  // Wrap selection with a span containing given style - PRESERVES existing HTML/formatting
  const wrapSelectionWithStyle = (cssText: string) => {
    focusEditor();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      // No selection - insert a styled placeholder
      insertHtml(`<span style="${cssText}">text</span>`);
      return;
    }
    
    // Clone the selection contents (preserves HTML structure)
    const fragment = range.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);
    const innerHtml = tempDiv.innerHTML;
    
    // Delete original selection and insert wrapped version
    range.deleteContents();
    const wrapper = document.createElement('span');
    wrapper.setAttribute('style', cssText);
    wrapper.innerHTML = innerHtml;
    range.insertNode(wrapper);
    
    // Move cursor after the inserted content
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(wrapper);
    newRange.collapse(false);
    sel.addRange(newRange);
    
    syncOut();
  };

  // Apply font family to selection
  const applyFont = (fontFamily: string) => {
    if (!fontFamily) return;
    wrapSelectionWithStyle(`font-family: ${fontFamily};`);
    setSelectedFont('');
  };

  // Apply font size to selection
  const applySize = (sizePt: string) => {
    if (!sizePt) return;
    wrapSelectionWithStyle(`font-size: ${sizePt}pt;`);
    setSelectedSize('');
  };

  // Apply color to selection
  const applyColor = (color: string) => {
    wrapSelectionWithStyle(`color: ${color};`);
  };
  
  // Insert vertical separator line (for same-row field separation)
  // Simple styled pipe character that stays inline with text
  const insertVerticalLine = () => {
    insertHtml('<span style="color: #999; margin: 0 6px;">|</span>');
  };

  const fontFamily = baseStyle.fontFamily === 'Kapra Neue'
    ? "'Kapra Neue', 'Oswald', sans-serif"
    : baseStyle.fontFamily || 'Arial, sans-serif';
  const fontSizePx = (baseStyle.fontSize || 12) * PT_TO_PX;

  return (
    <Box
      sx={{
        border: '1px solid rgba(27, 57, 103, 0.1)',
        borderRadius: 2.5,
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: '1px solid rgba(27, 57, 103, 0.08)',
          backgroundColor: 'rgba(27, 57, 103, 0.04)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary">
            Visuell editor. Markera text och välj formatering.
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label="Designläge" sx={{ bgcolor: 'rgba(27, 57, 103, 0.08)', color: FONTANA_COLORS.darkBlue }} />
            <Chip size="small" label="Stöd för {{metadataKey}}" variant="outlined" />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Typsnitt</InputLabel>
            <Select
              value={selectedFont}
              label="Typsnitt"
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const v = String(e.target.value || '');
                if (v) applyFont(v);
              }}
            >
              <MenuItem value="">
                <em>Välj…</em>
              </MenuItem>
              {RICH_TEXT_FONTS.map((f) => (
                <MenuItem key={f.value} value={f.value} sx={{ fontFamily: f.value }}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Storlek</InputLabel>
            <Select
              value={selectedSize}
              label="Storlek"
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const v = String(e.target.value || '');
                if (v) applySize(v);
              }}
            >
              <MenuItem value="">
                <em>pt</em>
              </MenuItem>
              {RICH_TEXT_SIZES.map((s) => (
                <MenuItem key={s} value={String(s)}>
                  {s}pt
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Textfärg - välj färg, sedan klicka Applicera">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ border: '1px solid rgba(27, 57, 103, 0.16)', borderRadius: 1.5, px: 0.5, display: 'flex', alignItems: 'center', bgcolor: '#fff' }}>
                <input
                  type="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyColor(selectedColor)}
                sx={{ minWidth: 74, bgcolor: '#fff', fontSize: '0.75rem' }}
              >
                Applicera
              </Button>
            </Box>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          {Object.entries(FONTANA_COLORS).map(([label, color]) => (
            <Tooltip key={`wysiwyg-${label}`} title={`Applicera ${label}`}>
              <Box
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSelectedColor(color);
                  applyColor(color);
                }}
                sx={{
                  ...COLOR_SWATCH_BUTTON_SX,
                  backgroundColor: color,
                }}
              />
            </Tooltip>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', p: 0.6, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.82)', border: '1px solid rgba(27, 57, 103, 0.08)' }}>
          <Tooltip title="Fet (Ctrl+B)">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('bold')}
              sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#e7f0fb' } }}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kursiv (Ctrl+I)">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('italic')}
              sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#e7f0fb' } }}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Understruken (Ctrl+U)">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('underline')}
              sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#e7f0fb' } }}
            >
              <span style={{ textDecoration: 'underline', fontWeight: 'bold', fontSize: 14 }}>U</span>
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="VERSALER (markera text först)">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => wrapSelectionWithStyle('text-transform: uppercase;')}
              sx={{ minWidth: 40, bgcolor: 'white' }}
            >
              ABC
            </Button>
          </Tooltip>
          <Tooltip title="gemener (markera text först)">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => wrapSelectionWithStyle('text-transform: lowercase;')}
              sx={{ minWidth: 40, bgcolor: 'white' }}
            >
              abc
            </Button>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Radbrytning (en rad)">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertHtml('<br/>')}
              sx={{ minWidth: 36, bgcolor: 'white' }}
            >
              ↵
            </Button>
          </Tooltip>
          <Tooltip title="Dubbel radbrytning (två rader)">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertHtml('<br/><br/>')}
              sx={{ minWidth: 36, bgcolor: 'white' }}
            >
              ↵↵
            </Button>
          </Tooltip>
          <Tooltip title="Lodrätt streck (separator mellan fält på samma rad)">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={insertVerticalLine}
              sx={{ minWidth: 36, bgcolor: 'white', fontWeight: 'bold' }}
            >
              |
            </Button>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Prickad horisontell linje">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertHtml('<div style="border-top: 1px dotted #999; width: 100%; margin: 8px 0;" contenteditable="false"></div><br/>')}
              sx={{ minWidth: 36, bgcolor: 'white' }}
            >
              ⋯
            </Button>
          </Tooltip>
          <Tooltip title="Streckad horisontell linje">
            <Button
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertHtml('<div style="border-top: 1px dashed #999; width: 100%; margin: 8px 0;" contenteditable="false"></div><br/>')}
              sx={{ minWidth: 36, bgcolor: 'white' }}
            >
              --
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={syncOut}
          onBlur={() => {
            saveSelection();
            syncOut();
          }}
          onMouseUp={saveSelection}
          onKeyUp={saveSelection}
          onFocus={saveSelection}
          sx={{
            border: '1px solid rgba(27, 57, 103, 0.14)',
            borderRadius: 2,
            p: 1.5,
            minHeight: 180,
            maxHeight: 320,
            overflow: 'auto',
            fontFamily,
            fontSize: `${fontSizePx}px`,
            color: baseStyle.color || '#000000',
            lineHeight: baseStyle.lineHeight || 1.4,
            bgcolor: 'white',
            boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.05)',
            '&:focus': {
              outline: 'none',
              borderColor: '#1976d2',
              boxShadow: '0 0 0 3px rgba(25,118,210,0.15)',
            },
          }}
        />

        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <InputLabel>Infoga datafält</InputLabel>
          <Select
            value={insertField}
            label="Infoga datafält"
            onChange={(e) => {
              const key = String(e.target.value || '');
              if (!key) return;
              insertText(`{{${key}}}`);
              setInsertField('');
            }}
          >
            <MenuItem value="">
              <em>Välj fält att infoga…</em>
            </MenuItem>
            {availableFields.map((field) => (
              <MenuItem key={field.id} value={field.id}>
                {field.label} ({field.templateName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tips: markera text for formatering. Datafält skrivs som {"{{fältnamn}}"} eller {"{fältnamn}"}. Radavstånd, padding och global typografi styr du i panelen Typografi.
        </Typography>
      </Box>
    </Box>
  );
};

const PdfTemplatesPage: React.FC = () => {
  // ========== State ==========
  const [templates, setTemplates] = useState<IPdfTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Current template being edited
  const [currentTemplate, setCurrentTemplate] = useState<IPdfTemplate>({ ...DEFAULT_PDF_TEMPLATE } as IPdfTemplate);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  
  // Canvas state
  const [zoom, setZoom] = useState(0.8);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; elX: number; elY: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [panMode, setPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number } | null>(null);

  // Slot drag/resize (multi mode)
  const [isDraggingSlot, setIsDraggingSlot] = useState(false);
  const [isResizingSlot, setIsResizingSlot] = useState(false);
  const [slotDragStart, setSlotDragStart] = useState<{ x: number; y: number; slotX: number; slotY: number } | null>(null);
  const [slotResizeStart, setSlotResizeStart] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  // Grid / snapping
  const [gridSizeMm, setGridSizeMm] = useState<number>(10);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(true);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [snapGuides, setSnapGuides] = useState<{ verticalMm: number[]; horizontalMm: number[] }>({
    verticalMm: [],
    horizontalMm: [],
  });

  // Undo/Redo history (per-template)
  const undoStackRef = useRef<IPdfTemplate[]>([]);
  const redoStackRef = useRef<IPdfTemplate[]>([]);
  const prevTemplateRef = useRef<IPdfTemplate | null>(null);
  const isHistoryActionRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Preview state
  const [previewAssetId, setPreviewAssetId] = useState<string>('');
  const [previewAsset, setPreviewAsset] = useState<IAsset | null>(null); // Full asset data for live preview
  const [multiPreviewAssets, setMultiPreviewAssets] = useState<IAsset[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [lastPreviewKey, setLastPreviewKey] = useState<string>('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOptions, setAssetOptions] = useState<IAsset[]>([]);
  const [previewPriorityCategoryIds, setPreviewPriorityCategoryIds] = useState<Set<string>>(() => new Set());
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [workspaceViewMode, setWorkspaceViewMode] = useState<'design' | 'split' | 'preview'>('design');
  const [autoPreviewEnabled, setAutoPreviewEnabled] = useState(false);
  
  // Banner/static image search state
  const [bannerImageSearch, setBannerImageSearch] = useState('');
  const [bannerImageOptions, setBannerImageOptions] = useState<IAsset[]>([]);
  
  // Field templates (for textField binding)
  const [fieldTemplates, setFieldTemplates] = useState<ITemplate[]>([]);

  // Product blocks (for multi-product slots)
  const [blockTemplates, setBlockTemplates] = useState<IPdfBlockTemplate[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(false);

  // Right panel width (resizable)
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(320);
  const rightPanelResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [isResizingRightPanel, setIsResizingRightPanel] = useState(false);
  
  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // Preset templates menu
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsDialog, setSettingsDialog] = useState<PdfSettingsDialog>(null);
  const [activePanel, setActivePanel] = useState<PdfPanelId>('templates');
  const [templateViewMode, setTemplateViewMode] = useState<'grid' | 'list'>('grid');
  const [richTextEditorMode, setRichTextEditorMode] = useState<'design' | 'code'>('design');
  
  // Refs
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  
  // Images in <img> cannot send Authorization headers. The backend supports JWT as
  // a `token` query param for preview endpoints, so we append it for local /api URLs.
  const withImgToken = useCallback((url: string) => {
    if (!url) return url;
    // Never leak token to external URLs
    if (!url.startsWith('/api/')) return url;
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}token=${encodeURIComponent(token)}`;
    } catch {
      return url;
    }
  }, []);

  // ========== Computed ==========
  const isLandscape = currentTemplate.page.orientation === 'landscape';
  const pageWidthMm = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
  const pageHeightMm = isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM;
  const pageWidthPx = pageWidthMm * MM_TO_PX_BASE * zoom;
  const pageHeightPx = pageHeightMm * MM_TO_PX_BASE * zoom;
  const contentBoxMm = useMemo(() => getContentBoxMm(currentTemplate.page), [currentTemplate.page]);
  const contentWidthMm = contentBoxMm.widthMm;
  const contentHeightMm = contentBoxMm.heightMm;
  const contentOffsetXPx = contentBoxMm.xMm * MM_TO_PX_BASE * zoom;
  const contentOffsetYPx = contentBoxMm.yMm * MM_TO_PX_BASE * zoom;
  const contentWidthPx = contentWidthMm * MM_TO_PX_BASE * zoom;
  const contentHeightPx = contentHeightMm * MM_TO_PX_BASE * zoom;
  
  const selectedElement = useMemo(() => {
    if (!selectedElementId) return null;
    return currentTemplate.elements.find(el => el.id === selectedElementId) || null;
  }, [currentTemplate.elements, selectedElementId]);

  const selectedSlot = useMemo(() => {
    if (!selectedSlotId) return null;
    return (currentTemplate.productSlots || []).find((s) => s.id === selectedSlotId) || null;
  }, [currentTemplate.productSlots, selectedSlotId]);
  const isRichTextElement = selectedElement?.kind === 'richText';
  const templateValidationIssues = useMemo(
    () => getMultiPageValidationIssues(currentTemplate),
    [currentTemplate]
  );
  const templateValidationErrors = templateValidationIssues.filter((issue) => issue.level === 'error');
  const templateValidationWarnings = templateValidationIssues.filter((issue) => issue.level === 'warning');
  const pagePreviewAsset = currentTemplate.templateMode === 'multi' ? null : previewAsset;
  const currentPreviewKey = useMemo(
    () => getTemplatePreviewKey(currentTemplate, previewAssetId, multiPreviewAssets),
    [currentTemplate, previewAssetId, multiPreviewAssets]
  );
  const multiPreviewAssetIds = useMemo(
    () => multiPreviewAssets.map((asset) => asset._id),
    [multiPreviewAssets]
  );
  const hasPreviewSource = useMemo(() => {
    if (currentTemplate.templateMode === 'multi') {
      return multiPreviewAssets.length > 0 || Boolean(previewAssetId);
    }
    return Boolean(previewAssetId);
  }, [currentTemplate.templateMode, previewAssetId, multiPreviewAssets.length]);
  const previewIsStale = Boolean(previewUrl) && lastPreviewKey !== '' && lastPreviewKey !== currentPreviewKey;
  const sortedElements = useMemo(
    () => [...currentTemplate.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [currentTemplate.elements]
  );
  const layerOrderedElements = useMemo(
    () => getLayerOrderedElements(currentTemplate.elements),
    [currentTemplate.elements]
  );
  const selectedSlotIndex = useMemo(
    () => (selectedSlotId ? (currentTemplate.productSlots || []).findIndex((slot) => slot.id === selectedSlotId) : -1),
    [currentTemplate.productSlots, selectedSlotId]
  );
  const sidebarQuery = sidebarSearch.trim().toLowerCase();
  const visibleTemplates = useMemo(() => {
    return templates
      .filter((template) => template.type === currentTemplate.type)
      .filter((template) => {
        if (!sidebarQuery) return true;
        return [template.name, template.description, template.page.orientation]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(sidebarQuery));
      });
  }, [currentTemplate.type, sidebarQuery, templates]);
  const visibleLayers = useMemo(() => {
    return layerOrderedElements.filter((element) => {
      if (!sidebarQuery) return true;
      const matchText = [
        ELEMENT_KIND_LABELS[element.kind],
        element.binding?.metadataKey,
        element.binding?.fallbackValue,
        element.content,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return matchText.includes(sidebarQuery);
    });
  }, [layerOrderedElements, sidebarQuery]);
  const visibleSlots = useMemo(() => {
    return (currentTemplate.productSlots || [])
      .map((slot, index) => {
        const block = slot.blockTemplateId
          ? blockTemplates.find((candidate) => candidate._id === slot.blockTemplateId) || null
          : null;
        const assigned = getAssignedMultiSlotAsset(multiPreviewAssets, index);
        return { slot, index, block, assigned };
      })
      .filter(({ block, assigned, index }) => {
        if (!sidebarQuery) return true;
        const matchText = [
          `produktplats ${index + 1}`,
          block?.name,
          assigned?.title,
          assigned?.fileName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return matchText.includes(sidebarQuery);
      });
  }, [blockTemplates, currentTemplate.productSlots, multiPreviewAssets, sidebarQuery]);
  const sidebarSearchLabel =
    activePanel === 'templates'
      ? 'Sök mallar'
      : activePanel === 'layers'
        ? 'Sök lager'
        : activePanel === 'tools'
          ? 'Sök produktplatser'
          : 'Sök';
  const sidebarSearchPlaceholder =
    activePanel === 'templates'
      ? 'Namn, beskrivning eller orientering'
      : activePanel === 'layers'
        ? 'Elementtyp, innehåll eller databindning'
        : activePanel === 'tools'
          ? 'Slot, block eller previewprodukt'
          : '';
  const selectionSummary = selectedElement
    ? `${ELEMENT_KIND_LABELS[selectedElement.kind]} markerad`
    : selectedSlot
      ? `Produktplats ${selectedSlotIndex >= 0 ? selectedSlotIndex + 1 : ''} markerad`
      : 'Ingen markering';
  const previewSelectionSummary = currentTemplate.templateMode === 'multi'
    ? `${multiPreviewAssets.length} previewprodukter`
    : previewAsset
      ? (previewAsset.title || previewAsset.fileName || '1 previewprodukt')
      : 'Ingen previewprodukt';
  const selectedElementPanelMatches = Boolean(selectedElement) && (
    activePanel === 'elements'
    || (activePanel === 'text' && ['textField', 'staticText', 'richText', 'bulletList'].includes(selectedElement!.kind))
    || (activePanel === 'assets' && ['productImage', 'staticImage', 'logo'].includes(selectedElement!.kind))
  );

  const cloneTemplate = useCallback((t: IPdfTemplate): IPdfTemplate => {
    // Safe deep-clone for undo/redo snapshots (templates are JSON-serializable)
    return JSON.parse(JSON.stringify(t)) as IPdfTemplate;
  }, []);

  // Keep undo history per template (switching template resets stacks automatically)
  useEffect(() => {
    const prev = prevTemplateRef.current;
    if (!prev) {
      prevTemplateRef.current = cloneTemplate(currentTemplate);
      return;
    }

    const prevKey = String((prev as any)._id || 'new');
    const currKey = String((currentTemplate as any)._id || 'new');
    if (prevKey !== currKey) {
      undoStackRef.current = [];
      redoStackRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
      prevTemplateRef.current = cloneTemplate(currentTemplate);
      return;
    }

    if (isHistoryActionRef.current) {
      isHistoryActionRef.current = false;
      prevTemplateRef.current = cloneTemplate(currentTemplate);
      return;
    }

    undoStackRef.current.push(prev);
    if (undoStackRef.current.length > 60) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(false);
    prevTemplateRef.current = cloneTemplate(currentTemplate);
  }, [currentTemplate, cloneTemplate]);

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const prev = stack.pop()!;
    redoStackRef.current.push(cloneTemplate(currentTemplate));
    setCanUndo(stack.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    isHistoryActionRef.current = true;
    setSelectedElementId(null);
    setSelectedSlotId(null);
    setCurrentTemplate(prev);
  }, [cloneTemplate, currentTemplate]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    if (stack.length === 0) return;
    const next = stack.pop()!;
    undoStackRef.current.push(cloneTemplate(currentTemplate));
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(stack.length > 0);
    isHistoryActionRef.current = true;
    setSelectedElementId(null);
    setSelectedSlotId(null);
    setCurrentTemplate(next);
  }, [cloneTemplate, currentTemplate]);
  
  // Get fields for current template type
  const availableFields = useMemo(() => {
    return buildAvailableFields(fieldTemplates, currentTemplate.type);
  }, [fieldTemplates, currentTemplate.type]);
  
  // ========== Data Loading ==========
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPdfTemplates();
      // Normalize all templates to ensure they have required properties
      const normalizedData = data.map((template) => normalizeTemplate(template, 'persisted'));
      setTemplates(normalizedData);
      
      // Keep the workspace in its default blank template until the user explicitly chooses a saved template.
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadFieldTemplates = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/templates');
      setFieldTemplates(data);
    } catch (err) {
      console.error('Failed to load field templates:', err);
    }
  }, []);

  const loadBlockTemplates = useCallback(async (type: 'products' | 'wine') => {
    try {
      setBlocksLoading(true);
      const data = await getPdfBlocks(type);
      setBlockTemplates(data || []);
    } catch (err) {
      console.error('Failed to load product blocks:', err);
      setBlockTemplates([]);
    } finally {
      setBlocksLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadTemplates();
    loadFieldTemplates();
  }, [loadFieldTemplates, loadTemplates]);

  useEffect(() => {
    let cancelled = false;

    const loadPreviewPriorityCategories = async () => {
      try {
        const { data } = await axiosInstance.get('/api/categories');
        const categories: PreviewSearchCategory[] = Array.isArray(data) ? data : [];
        const priorityIds = buildPreviewPriorityCategoryIds(categories);
        if (!cancelled) {
          setPreviewPriorityCategoryIds(priorityIds);
        }
      } catch (err) {
        console.error('Preview category priority load error:', err);
      }
    };

    loadPreviewPriorityCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadBlockTemplates(currentTemplate.type);
  }, [currentTemplate.type, loadBlockTemplates]);
  
  // Asset search (for preview)
  useEffect(() => {
    if (!assetSearch || assetSearch.length < 2) {
      setAssetOptions([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets', {
          params: { search: assetSearch, limit: 60, fileTypes: 'image' },
        });
        const list: IAsset[] = Array.isArray(data) ? data : (data.assets || data.data || []);
        setAssetOptions(sortPreviewAssetOptions(list, assetSearch, previewPriorityCategoryIds));
      } catch (err) {
        console.error('Asset search error:', err);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [assetSearch, previewPriorityCategoryIds]);
  
  // Banner/static image search (searches all images in DAM)
  useEffect(() => {
    if (!bannerImageSearch || bannerImageSearch.length < 2) {
      setBannerImageOptions([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets', {
          params: { 
            search: bannerImageSearch, 
            limit: 30,
            fileType: 'image', // Only images
          },
        });
        setBannerImageOptions(data.assets || data || []);
      } catch (err) {
        console.error('Banner image search error:', err);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [bannerImageSearch]);
  
  // Load full asset data when preview asset is selected
  useEffect(() => {
    if (!previewAssetId) {
      setPreviewAsset(null);
      return;
    }
    
    const loadAsset = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/assets/${previewAssetId}`);
        setPreviewAsset(data);
      } catch (err) {
        console.error('Error loading preview asset:', err);
        setPreviewAsset(null);
      }
    };
    
    loadAsset();
  }, [previewAssetId]);

  // Auto-pick a default preview product (artikelnummer 97034) whenever a
  // single-product template has no selected preview asset.
  const defaultPreviewLoadingRef = useRef(false);
  useEffect(() => {
    if (currentTemplate.templateMode === 'multi' || previewAssetId || defaultPreviewLoadingRef.current) return;
    let cancelled = false;
    defaultPreviewLoadingRef.current = true;

    (async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets', {
          params: { search: DEFAULT_PREVIEW_ARTIKELNUMMER, limit: 10, fileTypes: 'image' },
        });
        const list: any[] = Array.isArray(data) ? data : (data?.data || data?.assets || []);
        const matchesArtikelnummer = (asset: any) => {
          const meta = asset?.metadata;
          const value = meta instanceof Map ? meta.get('artikelnummer') : meta?.artikelnummer;
          return String(value || '').trim() === DEFAULT_PREVIEW_ARTIKELNUMMER;
        };
        const isUsableImage = (asset: any) => {
          const fileName = String(asset?.fileName || '').toLowerCase();
          if (fileName.endsWith('.tif') || fileName.endsWith('.tiff')) return false;
          if (/ean[-_ ]?13/i.test(asset?.title || '')) return false;
          return true;
        };
        const chosen =
          list.find((asset) => matchesArtikelnummer(asset) && isUsableImage(asset)) ||
          list.find(matchesArtikelnummer) ||
          list[0];
        if (!cancelled && chosen?._id) {
          setPreviewAssetId(chosen._id);
          setPreviewAsset(chosen as IAsset);
          setAssetOptions((prev) => {
            if (prev.some((asset) => asset._id === chosen._id)) return prev;
            return [chosen as IAsset, ...prev];
          });
        }
      } catch (err) {
        console.warn('[PdfTemplatesPage] Could not auto-select default preview product:', err);
      } finally {
        defaultPreviewLoadingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentTemplate._id, currentTemplate.templateMode, previewAssetId]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    if (selectedElement?.kind !== 'richText') {
      setRichTextEditorMode('design');
    }
  }, [selectedElement?.id, selectedElement?.kind]);

  const clearPreview = useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    const activeUrl = previewUrlRef.current;
    if (activeUrl) {
      URL.revokeObjectURL(activeUrl);
    }
    previewUrlRef.current = null;
    setPreviewUrl(null);
    setPreviewError(null);
    setLastPreviewKey('');
  }, []);

  const clearAllPreviewAssets = useCallback(() => {
    setMultiPreviewAssets([]);
    setPreviewAssetId('');
    setPreviewAsset(null);
    clearPreview();
  }, [clearPreview]);

  const handleDownloadPreview = useCallback(() => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    const safeName = (currentTemplate.name || 'pdf-preview')
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/\s+/g, '-');
    link.href = previewUrl;
    link.download = `${safeName || 'pdf-preview'}.pdf`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [currentTemplate.name, previewUrl]);

  const addMultiPreviewAsset = useCallback(async (assetId: string) => {
    try {
      const { data } = await axiosInstance.get(`/api/assets/${assetId}`);
      setPreviewAssetId((prev) => prev || data._id);
      setMultiPreviewAssets((prev) => {
        if (prev.some((a) => a._id === data._id)) return prev;
        return [...prev, data].slice(0, 20);
      });
    } catch (err) {
      console.error('Error loading multi preview asset:', err);
    }
  }, []);

  const removeMultiPreviewAsset = useCallback((assetId: string) => {
    setMultiPreviewAssets((prev) => {
      const next = prev.filter((asset) => asset._id !== assetId);
      if (next.length === 0) {
        setPreviewAssetId('');
        setPreviewAsset(null);
      } else if (previewAssetId === assetId) {
        setPreviewAssetId(next[0]._id);
      }
      return next;
    });
  }, [previewAssetId]);
  
  // ========== Template CRUD ==========
  const handleSave = useCallback(async () => {
    if (!currentTemplate.name.trim()) {
      setToast({ open: true, message: 'Ange ett namn för mallen', severity: 'error' });
      return;
    }

    if (currentTemplate.templateMode === 'multi' && currentTemplate.isActive !== false && templateValidationErrors.length > 0) {
      setToast({
        open: true,
        message: templateValidationErrors[0]?.message || 'Mallen innehåller fel som måste rättas innan den kan vara aktiv.',
        severity: 'error',
      });
      return;
    }
    
    setSaving(true);
    try {
      const maxProducts =
        currentTemplate.templateMode === 'multi'
          ? ((currentTemplate.productSlots && currentTemplate.productSlots.length > 0)
              ? currentTemplate.productSlots.length
              : currentTemplate.layout
                ? buildSlotsFromLayout(currentTemplate.page, currentTemplate.layout).length
                : 0)
          : undefined;
      const templateToSave = {
        ...serializeTemplate(currentTemplate),
        maxProducts,
      };

      if (currentTemplate._id) {
        const updated = normalizeTemplate(await updatePdfTemplate(currentTemplate._id, templateToSave), 'persisted');
        setTemplates(prev => prev.map(t => t._id === updated._id ? updated : t));
        setCurrentTemplate(updated);
        setToast({
          open: true,
          message: templateValidationWarnings.length > 0 ? 'Mall sparad med varningar' : 'Mall sparad!',
          severity: templateValidationWarnings.length > 0 ? 'info' : 'success',
        });
      } else {
        const created = normalizeTemplate(await createPdfTemplate(templateToSave), 'persisted');
        setTemplates(prev => [...prev, created]);
        setCurrentTemplate(created);
        setToast({
          open: true,
          message: templateValidationWarnings.length > 0 ? 'Mall skapad med varningar' : 'Mall skapad!',
          severity: templateValidationWarnings.length > 0 ? 'info' : 'success',
        });
      }
    } catch (err: any) {
      setToast({ open: true, message: err.message || 'Kunde inte spara', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [currentTemplate, templateValidationErrors, templateValidationWarnings]);
  
  const handleDelete = async () => {
    if (!currentTemplate._id) return;
    
    if (!window.confirm('Är du säker på att du vill ta bort denna mall?')) return;
    
    try {
      await deletePdfTemplate(currentTemplate._id);
      setTemplates(prev => prev.filter(t => t._id !== currentTemplate._id));
      
      // Select another template or create new
      const remaining = templates.filter(t => t._id !== currentTemplate._id);
      if (remaining.length > 0) {
        clearPreview();
        setCurrentTemplate(normalizeTemplate(remaining[0]));
      } else {
        clearPreview();
        setCurrentTemplate(normalizeTemplate({ ...DEFAULT_PDF_TEMPLATE }));
      }
      
      setSelectedElementId(null);
      setSelectedSlotId(null);
      setToast({ open: true, message: 'Mall borttagen', severity: 'success' });
    } catch (err: any) {
      setToast({ open: true, message: err.message || 'Kunde inte ta bort', severity: 'error' });
    }
  };
  
  const handleDuplicate = () => {
    clearPreview();
    setCurrentTemplate(normalizeTemplate({
      ...currentTemplate,
      _id: undefined,
      name: `${currentTemplate.name} (kopia)`,
    }));
    setSelectedElementId(null);
    setSelectedSlotId(null);
    setToast({ open: true, message: 'Mall duplicerad - spara för att skapa', severity: 'info' });
  };
  
  const handleNewTemplate = () => {
    clearPreview();
    setCurrentTemplate(normalizeTemplate({
      ...DEFAULT_PDF_TEMPLATE,
      type: currentTemplate.type, // Keep same type
    }));
    setSelectedElementId(null);
    setSelectedSlotId(null);
  };

  const ensureMultiDefaults = (t: IPdfTemplate): IPdfTemplate => {
    const preset = ((t.layout?.preset || '3x2') as LayoutPreset);
    const presetCfg = LAYOUT_PRESETS[preset] || LAYOUT_PRESETS['3x2'];
    const layout: IMultiProductLayout = {
      preset,
      rows: t.layout?.rows || presetCfg.rows,
      cols: t.layout?.cols || presetCfg.cols,
      gapMm: t.layout?.gapMm ?? 5,
    };

    const slots = (t.productSlots && t.productSlots.length > 0)
      ? t.productSlots
      : buildSlotsFromLayout(t.page, layout);

    return {
      ...t,
      templateMode: 'multi',
      layout,
      productSlots: slots,
      maxProducts: slots.length,
    };
  };

  const regenerateSlotsFromLayout = () => {
    setCurrentTemplate((prev) => {
      const next = ensureMultiDefaults(prev);
      const layout = next.layout!;
      const productSlots = buildSlotsFromLayout(next.page, layout);
      return { ...next, productSlots, maxProducts: productSlots.length };
    });
    setSelectedSlotId(null);
  };
  
  // Load a preset template
  const loadPresetTemplate = (presetIndex: number) => {
    const preset = PRESET_TEMPLATES[presetIndex];
    if (!preset) return;
    
    // Generate new IDs for all elements to avoid conflicts
    const elementsWithNewIds = preset.template.elements.map(el => ({
      ...el,
      id: generateId(),
    }));
    
    clearPreview();
    setCurrentTemplate(normalizeTemplate({
      ...preset.template,
      _id: undefined, // This is a new template, not saved yet
      elements: elementsWithNewIds,
    }, 'persisted'));
    setSelectedElementId(null);
    setPresetMenuAnchor(null);
    setToast({ open: true, message: `Mall "${preset.name}" laddad - spara för att skapa`, severity: 'info' });
  };
  
  // ========== Element Operations ==========
  const addElement = (kind: PdfElementKind) => {
    const preset = ELEMENT_PRESETS[kind];
    const newElement: IPdfElement = {
      ...preset,
      id: generateId(),
      kind,
      xMm: preset.xMm || 10,
      yMm: preset.yMm || 10,
      wMm: preset.wMm || 50,
      hMm: preset.hMm || 20,
      style: { ...preset.style },
      zIndex: currentTemplate.elements.length,
    } as IPdfElement;
    
    setCurrentTemplate(prev => ({
      ...prev,
      elements: [...prev.elements, newElement],
    }));
    setSelectedElementId(newElement.id);
    setSelectedSlotId(null);
  };
  
  const updateElement = useCallback((id: string, updates: Partial<IPdfElement>) => {
    setCurrentTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      ),
    }));
  }, []);
  
  const updateElementStyle = useCallback((id: string, styleUpdates: Partial<IPdfElementStyle>) => {
    setCurrentTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === id ? { ...el, style: { ...el.style, ...styleUpdates } } : el
      ),
    }));
  }, []);
  
  const deleteElement = useCallback((id: string) => {
    setCurrentTemplate(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== id),
    }));
    setSelectedElementId((prev) => (prev === id ? null : prev));
  }, []);

  const duplicateElement = useCallback((id: string) => {
    const source = currentTemplate.elements.find((element) => element.id === id);
    if (!source) return;
    const duplicate: IPdfElement = {
      ...source,
      id: generateId(),
      xMm: Math.min(contentWidthMm - source.wMm, Math.max(0, source.xMm + 5)),
      yMm: Math.min(contentHeightMm - source.hMm, Math.max(0, source.yMm + 5)),
      zIndex: Math.max(...currentTemplate.elements.map((element) => element.zIndex ?? 0), 0) + 1,
    };
    setCurrentTemplate((prev) => ({
      ...prev,
      elements: [...prev.elements, duplicate],
    }));
    setSelectedElementId(duplicate.id);
    setSelectedSlotId(null);
  }, [contentHeightMm, contentWidthMm, currentTemplate.elements]);

  const toggleElementLock = useCallback((id: string) => {
    updateElement(id, { locked: !currentTemplate.elements.find((element) => element.id === id)?.locked });
  }, [currentTemplate.elements, updateElement]);

  const centerElementHorizontally = (id: string) => {
    const element = currentTemplate.elements.find((item) => item.id === id);
    if (!element) return;
    updateElement(id, {
      xMm: Math.round(((contentWidthMm - element.wMm) / 2) * 10) / 10,
    });
  };

  const centerElementVertically = (id: string) => {
    const element = currentTemplate.elements.find((item) => item.id === id);
    if (!element) return;
    updateElement(id, {
      yMm: Math.round(((contentHeightMm - element.hMm) / 2) * 10) / 10,
    });
  };

  const setZoomToFit = useCallback((mode: 'page' | 'width' | 'actual') => {
    if (mode === 'actual') {
      setZoom(1);
      return;
    }
    const container = canvasScrollRef.current;
    if (!container) return;
    const paddingPx = 96;
    const widthZoom = (container.clientWidth - paddingPx) / (pageWidthMm * MM_TO_PX_BASE);
    const heightZoom = (container.clientHeight - paddingPx) / (pageHeightMm * MM_TO_PX_BASE);
    const nextZoom = mode === 'width' ? widthZoom : Math.min(widthZoom, heightZoom);
    setZoom(Math.min(2, Math.max(0.3, Math.round(nextZoom * 100) / 100)));
  }, [pageHeightMm, pageWidthMm]);
  
  // Z-index ordering functions
  const moveLayer = useCallback((id: string, action: LayerMoveAction) => {
    const element = currentTemplate.elements.find((item) => item.id === id);
    if (element?.locked) {
      setToast({ open: true, message: 'Lås upp elementet innan du ändrar lagerordning', severity: 'info' });
      return;
    }
    setCurrentTemplate((prev) => ({ ...prev, elements: moveElementLayer(prev.elements, id, action) }));
    setSelectedElementId(id);
  }, [currentTemplate.elements]);

  const bringForward = (id: string) => moveLayer(id, 'forward');
  const sendBackward = (id: string) => moveLayer(id, 'backward');
  const bringToFront = (id: string) => moveLayer(id, 'front');
  const sendToBack = (id: string) => moveLayer(id, 'back');

  const applyDesignerSnap = useCallback((
    rect: { xMm: number; yMm: number; wMm: number; hMm: number },
    others: Array<{ xMm: number; yMm: number; wMm: number; hMm: number }>
  ) => {
    const thresholdMm = 2;

    const bestCandidate = (
      currentValue: number,
      candidates: Array<{ targetValue: number; guideMm: number }>
    ): { targetValue: number; guideMm: number } | null => {
      let best: { targetValue: number; guideMm: number } | null = null;
      let bestDistance = thresholdMm + 0.001;
      candidates.forEach((candidate) => {
        const distance = Math.abs(candidate.targetValue - currentValue);
        if (distance <= thresholdMm && distance < bestDistance) {
          best = candidate;
          bestDistance = distance;
        }
      });
      return best;
    };

    const xCandidates: Array<{ targetValue: number; guideMm: number }> = [
      { targetValue: 0, guideMm: 0 },
      { targetValue: Math.max(0, (contentWidthMm - rect.wMm) / 2), guideMm: contentWidthMm / 2 },
      { targetValue: Math.max(0, contentWidthMm - rect.wMm), guideMm: contentWidthMm },
      ...others.flatMap((other) => [
        { targetValue: other.xMm, guideMm: other.xMm },
        { targetValue: other.xMm + (other.wMm / 2) - (rect.wMm / 2), guideMm: other.xMm + (other.wMm / 2) },
        { targetValue: other.xMm + other.wMm - rect.wMm, guideMm: other.xMm + other.wMm },
      ]),
    ];
    const yCandidates: Array<{ targetValue: number; guideMm: number }> = [
      { targetValue: 0, guideMm: 0 },
      { targetValue: Math.max(0, (contentHeightMm - rect.hMm) / 2), guideMm: contentHeightMm / 2 },
      { targetValue: Math.max(0, contentHeightMm - rect.hMm), guideMm: contentHeightMm },
      ...others.flatMap((other) => [
        { targetValue: other.yMm, guideMm: other.yMm },
        { targetValue: other.yMm + (other.hMm / 2) - (rect.hMm / 2), guideMm: other.yMm + (other.hMm / 2) },
        { targetValue: other.yMm + other.hMm - rect.hMm, guideMm: other.yMm + other.hMm },
      ]),
    ];

    const snappedX = bestCandidate(rect.xMm, xCandidates);
    const snappedY = bestCandidate(rect.yMm, yCandidates);

    return {
      xMm: snappedX ? snappedX.targetValue : rect.xMm,
      yMm: snappedY ? snappedY.targetValue : rect.yMm,
      guides: {
        verticalMm: snappedX ? [snappedX.guideMm] : [],
        horizontalMm: snappedY ? [snappedY.guideMm] : [],
      },
    };
  }, [contentHeightMm, contentWidthMm]);
  
  // ========== Canvas Drag & Resize ==========
  const updateSlot = useCallback((slotId: string, updates: Partial<IProductSlot>) => {
    setCurrentTemplate((prev) => ({
      ...prev,
      productSlots: (prev.productSlots || []).map((s) => (s.id === slotId ? { ...s, ...updates } : s)),
    }));
  }, []);

  const handleSlotMouseDown = (e: React.MouseEvent, slotId: string, action: 'drag' | 'resize') => {
    if (panMode) return;
    e.preventDefault();
    e.stopPropagation();

    const slot = (currentTemplate.productSlots || []).find((s) => s.id === slotId);
    if (!slot) return;

    setSelectedSlotId(slotId);
    setSelectedElementId(null);

    if (action === 'drag') {
      setIsDraggingSlot(true);
      setSlotDragStart({
        x: e.clientX,
        y: e.clientY,
        slotX: slot.xMm,
        slotY: slot.yMm,
      });
    } else {
      setIsResizingSlot(true);
      setSlotResizeStart({
        x: e.clientX,
        y: e.clientY,
        w: slot.wMm,
        h: slot.hMm,
      });
    }
  };

  const handleCanvasMouseDown = (e: React.MouseEvent, elementId: string, action: 'drag' | 'resize') => {
    if (panMode) return;
    e.preventDefault();
    e.stopPropagation();

    const element = currentTemplate.elements.find(el => el.id === elementId);
    if (!element) return;
    
    setSelectedElementId(elementId);
    setSelectedSlotId(null);
    if (element.locked) return;
    
    if (action === 'drag') {
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        elX: element.xMm,
        elY: element.yMm,
      });
    } else {
      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        w: element.wMm,
        h: element.hMm,
      });
    }
  };
  
  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    const pxToMm = 1 / (MM_TO_PX_BASE * zoom);
    
    if (selectedElementId && isDragging && dragStart) {
      const dx = (e.clientX - dragStart.x) * pxToMm;
      const dy = (e.clientY - dragStart.y) * pxToMm;
      
      // Clamp to page bounds
      const element = currentTemplate.elements.find(el => el.id === selectedElementId);
      if (!element) return;
      
      let newX = Math.max(0, Math.min(contentWidthMm - element.wMm, dragStart.elX + dx));
      let newY = Math.max(0, Math.min(contentHeightMm - element.hMm, dragStart.elY + dy));
      if (snapToGrid && !e.altKey) {
        const others = currentTemplate.elements
          .filter((el) => el.id !== selectedElementId)
          .map((el) => ({ xMm: el.xMm, yMm: el.yMm, wMm: el.wMm, hMm: el.hMm }));
        const snapResult = applyDesignerSnap({ xMm: newX, yMm: newY, wMm: element.wMm, hMm: element.hMm }, others);
        newX = snapResult.guides.verticalMm.length > 0 ? snapResult.xMm : Math.round(newX / gridSizeMm) * gridSizeMm;
        newY = snapResult.guides.horizontalMm.length > 0 ? snapResult.yMm : Math.round(newY / gridSizeMm) * gridSizeMm;
        setSnapGuides(snapResult.guides);
      } else {
        setSnapGuides({ verticalMm: [], horizontalMm: [] });
      }
      if (!snapToGrid || e.altKey) {
        setSnapGuides({ verticalMm: [], horizontalMm: [] });
      }
      if (snapToGrid && !e.altKey) {
        newX = Math.max(0, Math.min(contentWidthMm - element.wMm, newX));
        newY = Math.max(0, Math.min(contentHeightMm - element.hMm, newY));
      }
      
      updateElement(selectedElementId, { xMm: Math.round(newX * 10) / 10, yMm: Math.round(newY * 10) / 10 });
    }
    
    if (selectedElementId && isResizing && resizeStart) {
      const dx = (e.clientX - resizeStart.x) * pxToMm;
      const dy = (e.clientY - resizeStart.y) * pxToMm;
      const element = currentTemplate.elements.find((el) => el.id === selectedElementId);
      if (!element) return;
      
      let newW = Math.min(contentWidthMm - element.xMm, Math.max(5, resizeStart.w + dx));
      let newH = Math.min(contentHeightMm - element.yMm, Math.max(5, resizeStart.h + dy));
      if (snapToGrid && !e.altKey) {
        newW = Math.min(contentWidthMm - element.xMm, Math.max(5, Math.round(newW / gridSizeMm) * gridSizeMm));
        newH = Math.min(contentHeightMm - element.yMm, Math.max(5, Math.round(newH / gridSizeMm) * gridSizeMm));
      }
      
      updateElement(selectedElementId, { 
        wMm: Math.round(newW * 10) / 10, 
        hMm: Math.round(newH * 10) / 10 
      });
    }

    if (selectedSlotId && isDraggingSlot && slotDragStart) {
      const dx = (e.clientX - slotDragStart.x) * pxToMm;
      const dy = (e.clientY - slotDragStart.y) * pxToMm;

      const slot = (currentTemplate.productSlots || []).find((s) => s.id === selectedSlotId);
      if (!slot) return;

      let newX = Math.max(0, Math.min(contentWidthMm - slot.wMm, slotDragStart.slotX + dx));
      let newY = Math.max(0, Math.min(contentHeightMm - slot.hMm, slotDragStart.slotY + dy));
      if (snapToGrid && !e.altKey) {
        const others = (currentTemplate.productSlots || [])
          .filter((candidate) => candidate.id !== selectedSlotId)
          .map((candidate) => ({ xMm: candidate.xMm, yMm: candidate.yMm, wMm: candidate.wMm, hMm: candidate.hMm }));
        const snapResult = applyDesignerSnap({ xMm: newX, yMm: newY, wMm: slot.wMm, hMm: slot.hMm }, others);
        newX = snapResult.guides.verticalMm.length > 0 ? snapResult.xMm : Math.round(newX / gridSizeMm) * gridSizeMm;
        newY = snapResult.guides.horizontalMm.length > 0 ? snapResult.yMm : Math.round(newY / gridSizeMm) * gridSizeMm;
        setSnapGuides(snapResult.guides);
      } else {
        setSnapGuides({ verticalMm: [], horizontalMm: [] });
      }
      if (snapToGrid && !e.altKey) {
        newX = Math.max(0, Math.min(contentWidthMm - slot.wMm, newX));
        newY = Math.max(0, Math.min(contentHeightMm - slot.hMm, newY));
      }

      updateSlot(selectedSlotId, {
        xMm: Math.round(newX * 10) / 10,
        yMm: Math.round(newY * 10) / 10,
      });
    }

    if (selectedSlotId && isResizingSlot && slotResizeStart) {
      const dx = (e.clientX - slotResizeStart.x) * pxToMm;
      const dy = (e.clientY - slotResizeStart.y) * pxToMm;
      const slot = (currentTemplate.productSlots || []).find((s) => s.id === selectedSlotId);
      if (!slot) return;

      let newW = Math.min(contentWidthMm - slot.xMm, Math.max(5, slotResizeStart.w + dx));
      let newH = Math.min(contentHeightMm - slot.yMm, Math.max(5, slotResizeStart.h + dy));
      if (snapToGrid && !e.altKey) {
        newW = Math.min(contentWidthMm - slot.xMm, Math.max(5, Math.round(newW / gridSizeMm) * gridSizeMm));
        newH = Math.min(contentHeightMm - slot.yMm, Math.max(5, Math.round(newH / gridSizeMm) * gridSizeMm));
      }

      updateSlot(selectedSlotId, {
        wMm: Math.round(newW * 10) / 10,
        hMm: Math.round(newH * 10) / 10,
      });
    }
  };
  
  const handleCanvasMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setDragStart(null);
    setResizeStart(null);
    setSnapGuides({ verticalMm: [], horizontalMm: [] });

    setIsDraggingSlot(false);
    setIsResizingSlot(false);
    setSlotDragStart(null);
    setSlotResizeStart(null);
  };

  const handlePanMouseDown = (e: React.MouseEvent) => {
    if (!panMode) return;
    const container = canvasScrollRef.current;
    if (!container) return;
    e.preventDefault();
    setIsPanning(true);
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: container.scrollLeft,
      startTop: container.scrollTop,
    };
  };

  useEffect(() => {
    if (!isPanning) return;
    const container = canvasScrollRef.current;
    if (!container) return;

    const onMove = (e: MouseEvent) => {
      const pan = panRef.current;
      if (!pan) return;
      const dx = e.clientX - pan.startX;
      const dy = e.clientY - pan.startY;
      container.scrollLeft = pan.startLeft - dx;
      container.scrollTop = pan.startTop - dy;
    };

    const onUp = () => {
      setIsPanning(false);
      panRef.current = null;
    };

    document.body.style.cursor = 'grabbing';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPanning]);

  // ========== Keyboard shortcuts ==========
  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;

      const isCtrl = e.ctrlKey || e.metaKey;
      const key = (e.key || '').toLowerCase();

      // Undo/redo
      if (isCtrl && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (isCtrl && (key === 'y' || (key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }

      // Save
      if (isCtrl && key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      if (isCtrl && key === 'd' && selectedElementId) {
        e.preventDefault();
        duplicateElement(selectedElementId);
        return;
      }

      if (isCtrl && key === 'l' && selectedElementId) {
        e.preventDefault();
        toggleElementLock(selectedElementId);
        return;
      }

      // Delete selected element/slot
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementId) {
          e.preventDefault();
          const selected = currentTemplate.elements.find((x) => x.id === selectedElementId);
          if (selected?.locked) {
            setToast({ open: true, message: 'Lås upp elementet innan du tar bort det', severity: 'info' });
            return;
          }
          deleteElement(selectedElementId);
          return;
        }
        if (selectedSlotId) {
          e.preventDefault();
          updateSlot(selectedSlotId, { blockTemplateId: undefined });
          return;
        }
      }

      // Nudge with arrow keys
      const isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (!isArrow) return;

      const step = e.shiftKey ? 5 : 1; // mm
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      if (dx === 0 && dy === 0) return;

      if (selectedElementId) {
        e.preventDefault();
        const el = currentTemplate.elements.find((x) => x.id === selectedElementId);
        if (!el) return;
        if (el.locked) return;
        const newX = Math.max(0, Math.min(contentWidthMm - el.wMm, el.xMm + dx));
        const newY = Math.max(0, Math.min(contentHeightMm - el.hMm, el.yMm + dy));
        updateElement(selectedElementId, { xMm: Math.round(newX * 10) / 10, yMm: Math.round(newY * 10) / 10 });
        return;
      }

      if (selectedSlotId) {
        e.preventDefault();
        const slot = (currentTemplate.productSlots || []).find((s) => s.id === selectedSlotId);
        if (!slot) return;
        const newX = Math.max(0, Math.min(contentWidthMm - slot.wMm, slot.xMm + dx));
        const newY = Math.max(0, Math.min(contentHeightMm - slot.hMm, slot.yMm + dy));
        updateSlot(selectedSlotId, { xMm: Math.round(newX * 10) / 10, yMm: Math.round(newY * 10) / 10 });
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    currentTemplate.elements,
    currentTemplate.productSlots,
    deleteElement,
    duplicateElement,
    handleSave,
    contentHeightMm,
    contentWidthMm,
    redo,
    selectedElementId,
    selectedSlotId,
    toggleElementLock,
    undo,
    updateElement,
    updateSlot,
  ]);
  
  // ========== Preview ==========
  const generatePreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      if (templateValidationErrors.length > 0) {
        const message = templateValidationErrors[0]?.message || 'Mallen innehåller fel som måste rättas innan preview kan genereras.';
        clearPreview();
        setPreviewError(message);
        setToast({ open: true, message, severity: 'error' });
        return;
      }

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }

      const previewTemplate = serializeTemplate(currentTemplate);

      if (currentTemplate.templateMode === 'multi') {
        const ids = multiPreviewAssets.length > 0
          ? multiPreviewAssets.map((a) => a._id)
          : previewAssetId ? [previewAssetId] : [];
        if (ids.length === 0) {
          const message = 'Välj minst en produkt för att generera multi-preview.';
          clearPreview();
          setPreviewError(message);
          setToast({ open: true, message, severity: 'info' });
          return;
        }
        const url = await getMultiPdfPreviewUrl(previewTemplate, ids);
        setPreviewUrl(url);
        previewUrlRef.current = url;
        setLastPreviewKey(currentPreviewKey);
        return;
      }

      if (!previewAssetId) {
        const message = 'Välj en produkt för att generera preview.';
        clearPreview();
        setPreviewError(message);
        setToast({ open: true, message, severity: 'info' });
        return;
      }

      const url = await getPdfPreviewUrl(previewTemplate, previewAssetId);
      setPreviewUrl(url);
      previewUrlRef.current = url;
      setLastPreviewKey(currentPreviewKey);
    } catch (err: any) {
      console.error('Preview error:', err);
      const message = await getPdfTemplateServiceErrorMessage(err, 'Kunde inte generera förhandsgranskning');
      setPreviewError(message);
      setToast({ open: true, message, severity: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  }, [clearPreview, currentPreviewKey, currentTemplate, previewAssetId, multiPreviewAssets, templateValidationErrors]);
  
  useEffect(() => {
    if (!hasPreviewSource) {
      clearPreview();
      return;
    }

    if (!autoPreviewEnabled) {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      return;
    }

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    previewTimeoutRef.current = setTimeout(() => {
      generatePreview();
    }, 700);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
    };
  }, [autoPreviewEnabled, clearPreview, currentPreviewKey, generatePreview, hasPreviewSource]);

  useEffect(() => {
    return () => {
      const activeUrl = previewUrlRef.current;
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, []);

  // Right panel resize handlers (drag left edge to resize)
  useEffect(() => {
    if (!isResizingRightPanel) return;

    const onMove = (e: MouseEvent) => {
      const start = rightPanelResizeRef.current;
      if (!start) return;
      const delta = start.startX - e.clientX; // drag left => wider
      const next = Math.min(720, Math.max(280, start.startWidth + delta));
      setRightPanelWidth(next);
    };

    const onUp = () => {
      setIsResizingRightPanel(false);
      rightPanelResizeRef.current = null;
    };

    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizingRightPanel]);
  
  // ========== Render ==========
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: 0,
        gap: 1,
        p: 0.75,
        borderRadius: 3,
        background: 'linear-gradient(180deg, #eef4fb 0%, #f6f8fc 100%)',
      }}
    >
      {/* Left Panel */}
      <Paper sx={{ ...PANEL_SX, width: 320, display: 'none', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>PDF-mallar</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En arbetsyta för mallar, lager, produktplatser och preview.
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip
              size="small"
              label={currentTemplate.templateMode === 'multi' ? 'Multi-mall' : '1 produkt'}
              sx={{ bgcolor: 'rgba(27, 57, 103, 0.08)', color: FONTANA_COLORS.darkBlue }}
            />
            <Chip
              size="small"
              label={`${visibleTemplates.length} mallar`}
              variant="outlined"
            />
          </Box>
          <ToggleButtonGroup
            value={currentTemplate.type}
            exclusive
            onChange={(_, value) => value && setCurrentTemplate(prev => ({ ...prev, type: value }))}
            size="small"
            fullWidth
            sx={{ ...SEGMENTED_TOGGLE_SX, mb: 1.5 }}
          >
            <ToggleButton value="products">Produkter</ToggleButton>
            <ToggleButton value="wine">Vin</ToggleButton>
          </ToggleButtonGroup>
          <Tabs
            value={['templates', 'layers', 'tools'].includes(activePanel) ? activePanel : 'layers'}
            onChange={(_, value) => setActivePanel(value)}
            variant="fullWidth"
            sx={SEGMENTED_TABS_SX}
          >
            <Tab value="templates" label="Mallar" />
            <Tab value="layers" label="Lager" />
            <Tab
              value="tools"
              label={currentTemplate.templateMode === 'multi' ? 'Produktplatser' : 'Platser'}
              wrapped
            />
          </Tabs>
          <TextField
            size="small"
            fullWidth
            label={sidebarSearchLabel}
            placeholder={sidebarSearchPlaceholder}
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            sx={{ mt: 1.5 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pb: 2 }}>
          {activePanel === 'templates' && (
            <List sx={{ py: 0 }}>
              {visibleTemplates.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(27, 57, 103, 0.18)',
                    borderRadius: 2,
                    px: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.65)',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {sidebarQuery ? 'Ingen mall matchar din filtrering.' : 'Det finns inga mallar för den här typen ännu.'}
                  </Typography>
                </Box>
              ) : (
                visibleTemplates.map(template => (
                  <ListItemButton
                    key={template._id}
                    selected={template._id === currentTemplate._id}
                    sx={SIDEBAR_ITEM_SX}
                    onClick={() => {
                      clearPreview();
                      setCurrentTemplate(normalizeTemplate(template));
                      setSelectedElementId(null);
                      setSelectedSlotId(null);
                    }}
                  >
                    <ListItemText
                      primary={template.name || 'Namnlös'}
                      secondary={`${template.page.orientation === 'landscape' ? 'Liggande' : 'Stående'}${template.description ? ` • ${template.description}` : ''}`}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          )}

          {activePanel === 'layers' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Välj element direkt från lagerlistan.
              </Typography>
              {visibleLayers.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(27, 57, 103, 0.18)',
                    borderRadius: 2,
                    px: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.65)',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {sidebarQuery
                      ? 'Ingen lagerpost matchar din filtrering.'
                      : 'Inga element ännu. Lägg till objekt från verktygsraden ovanför arbetsytan.'}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {visibleLayers.map((element) => (
                    <ListItemButton
                      key={element.id}
                      selected={element.id === selectedElementId}
                      sx={SIDEBAR_ITEM_SX}
                      onClick={() => {
                        setSelectedElementId(element.id);
                        setSelectedSlotId(null);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        {ELEMENT_ICONS[element.kind]}
                      </ListItemIcon>
                      <ListItemText
                        primary={ELEMENT_KIND_LABELS[element.kind]}
                        secondary={`${Math.round(element.wMm)} × ${Math.round(element.hMm)} mm • z ${element.zIndex ?? 0}`}
                      />
                      {element.locked && <LockIcon color="warning" fontSize="small" />}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}

          {activePanel === 'tools' && (
            <>
              {currentTemplate.templateMode !== 'multi' ? (
                <Alert severity="info">
                  Produktplatser används i multi-läge. Byt dokumentet till <strong>Multi</strong> för att skapa och placera slots.
                </Alert>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Produktplatser använder nu innehållsytan som koordinatsystem.
                  </Typography>
                  {visibleSlots.length === 0 ? (
                    <Box
                      sx={{
                        minHeight: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed rgba(27, 57, 103, 0.18)',
                        borderRadius: 2,
                        px: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255,255,255,0.65)',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {sidebarQuery ? 'Ingen produktplats matchar din filtrering.' : 'Inga produktplatser ännu.'}
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {visibleSlots.map(({ slot, index, block, assigned }) => (
                        <ListItemButton
                          key={slot.id}
                          selected={slot.id === selectedSlotId}
                          sx={SIDEBAR_ITEM_SX}
                          onClick={() => {
                            setSelectedSlotId(slot.id);
                            setSelectedElementId(null);
                          }}
                        >
                          <ListItemText
                            primary={`Produktplats ${index + 1}`}
                            secondary={`${Math.round(slot.wMm)} × ${Math.round(slot.hMm)} mm • ${block?.name || 'Inget block'}${assigned ? ` • ${assigned.title || assigned.fileName}` : ''}`}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleNewTemplate}
            fullWidth
            sx={{ mb: 1 }}
          >
            Ny mall
          </Button>

          <Button
            startIcon={<FolderOpenIcon />}
            variant="outlined"
            onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
            fullWidth
            sx={{ mb: 1 }}
            color="secondary"
          >
            Ladda färdig mall
          </Button>
          <Menu
            anchorEl={presetMenuAnchor}
            open={Boolean(presetMenuAnchor)}
            onClose={() => setPresetMenuAnchor(null)}
          >
            {PRESET_TEMPLATES.filter(p => p.template.type === currentTemplate.type).length === 0 && (
              <MenuItem disabled>
                <ListItemText
                  primary="Inga mallar för denna typ"
                  secondary={`Byt till ${currentTemplate.type === 'products' ? 'Vin' : 'Produkter'} för att se fler`}
                />
              </MenuItem>
            )}
            {PRESET_TEMPLATES.map((preset, idx) => (
              preset.template.type === currentTemplate.type && (
                <MenuItem key={idx} onClick={() => loadPresetTemplate(idx)}>
                  <ListItemText
                    primary={preset.name}
                    secondary={preset.description}
                  />
                </MenuItem>
              )
            ))}
          </Menu>

          {currentTemplate._id && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<ContentCopyIcon />}
                size="small"
                onClick={handleDuplicate}
                sx={{ flex: 1 }}
              >
                Duplicera
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                size="small"
                color="error"
                onClick={handleDelete}
                sx={{ flex: 1 }}
              >
                Ta bort
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <PdfToolRail
        elementIcons={ELEMENT_ICONS}
        elementLabels={ELEMENT_KIND_LABELS}
        templateMode={currentTemplate.templateMode}
        panMode={panMode}
        selectedElement={selectedElement}
        onAddElement={addElement}
        onTogglePan={() => setPanMode((v) => !v)}
        onDuplicateSelected={() => selectedElement && duplicateElement(selectedElement.id)}
        onToggleSelectedLock={() => selectedElement && toggleElementLock(selectedElement.id)}
      />
      
      {/* Center Panel - Canvas */}
      <Paper sx={{ ...PANEL_SX, flex: 1, minWidth: 0, minHeight: 0, p: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
          <Box sx={{ ...TOOLBAR_SURFACE_SX, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Mallnamn"
              value={currentTemplate.name}
              onChange={e => setCurrentTemplate(prev => ({ ...prev, name: e.target.value }))}
              sx={{ width: 240 }}
            />

            <ToggleButtonGroup
              value={currentTemplate.page.orientation}
              exclusive
              onChange={(_, value) => value && setCurrentTemplate(prev => ({
                ...prev,
                page: { ...prev.page, orientation: value },
              }))}
              size="small"
              sx={SEGMENTED_TOGGLE_SX}
            >
              <ToggleButton value="portrait">Stående</ToggleButton>
              <ToggleButton value="landscape">Liggande</ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToggleButtonGroup
              value={currentTemplate.templateMode || 'single'}
              exclusive
              onChange={(_, value) => {
                if (!value) return;
                const mode = value as TemplateMode;
                clearPreview();
                if (mode === 'multi') {
                  setCurrentTemplate((prev) => ensureMultiDefaults(prev));
                  setActivePanel('tools');
                } else {
                  setCurrentTemplate((prev) => ({ ...prev, templateMode: 'single', maxProducts: undefined }));
                  setSelectedSlotId(null);
                  setMultiPreviewAssets([]);
                }
                setSelectedElementId(null);
              }}
              size="small"
              sx={SEGMENTED_TOGGLE_SX}
            >
              <ToggleButton value="single">1 produkt</ToggleButton>
              <ToggleButton value="multi">Multi</ToggleButton>
            </ToggleButtonGroup>

            {currentTemplate.templateMode === 'multi' && (
              <>
                <FormControl size="small" sx={{ minWidth: 230 }}>
                  <InputLabel>Layout</InputLabel>
                  <Select
                    value={(currentTemplate.layout?.preset || '3x2') as LayoutPreset}
                    label="Layout"
                    onChange={(e) => {
                      const preset = e.target.value as LayoutPreset;
                      const presetCfg = LAYOUT_PRESETS[preset] || LAYOUT_PRESETS['3x2'];
                      const nextLayout: IMultiProductLayout = {
                        preset,
                        rows: presetCfg.rows,
                        cols: presetCfg.cols,
                        gapMm: currentTemplate.layout?.gapMm ?? 5,
                      };

                      const shouldRegen = window.confirm(
                        'Skapa om produktslots enligt denna layout?\n\nDetta ersätter nuvarande slot-placeringar.'
                      );

                      setCurrentTemplate((prev) => ({
                        ...ensureMultiDefaults({ ...prev, layout: nextLayout }),
                        productSlots: shouldRegen ? buildSlotsFromLayout(prev.page, nextLayout) : prev.productSlots,
                        maxProducts: shouldRegen
                          ? buildSlotsFromLayout(prev.page, nextLayout).length
                          : prev.productSlots?.length,
                      }));

                      setSelectedSlotId(null);
                      setSelectedElementId(null);
                    }}
                  >
                    {(Object.keys(LAYOUT_PRESETS) as LayoutPreset[]).map((preset) => (
                      <MenuItem key={preset} value={preset}>
                        {LAYOUT_PRESETS[preset].label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Tooltip title="Skapa om slots från layout">
                  <IconButton size="small" onClick={regenerateSlotsFromLayout}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <ToggleButtonGroup
              value={workspaceViewMode}
              exclusive
              onChange={(_, value) => value && setWorkspaceViewMode(value)}
              size="small"
              sx={SEGMENTED_TOGGLE_SX}
            >
              <ToggleButton value="design">Design</ToggleButton>
              <ToggleButton value="split">Delad vy</ToggleButton>
              <ToggleButton value="preview">Preview</ToggleButton>
            </ToggleButtonGroup>

            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
            >
              Inställningar
            </Button>
            <Menu
              anchorEl={settingsMenuAnchor}
              open={Boolean(settingsMenuAnchor)}
              onClose={() => setSettingsMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setSettingsDialog('template'); setSettingsMenuAnchor(null); }}>Mallinställningar</MenuItem>
              <MenuItem onClick={() => { setSettingsDialog('page'); setSettingsMenuAnchor(null); }}>Sidinställningar</MenuItem>
              <MenuItem onClick={() => { setSettingsDialog('validation'); setSettingsMenuAnchor(null); }}>Status och validering</MenuItem>
              <MenuItem onClick={() => { setSettingsDialog('shortcuts'); setSettingsMenuAnchor(null); }}>Kortkommandon</MenuItem>
              <Divider />
              <MenuItem onClick={() => { setPresetMenuAnchor(settingsMenuAnchor); setSettingsMenuAnchor(null); }}>Ladda färdig mall</MenuItem>
              <MenuItem onClick={() => { handleDuplicate(); setSettingsMenuAnchor(null); }} disabled={!currentTemplate._id}>Duplicera mall</MenuItem>
              <MenuItem onClick={() => { setSettingsMenuAnchor(null); handleDelete(); }} disabled={!currentTemplate._id}>Ta bort mall</MenuItem>
            </Menu>
          </Box>

          {workspaceViewMode !== 'preview' && (
            <>
              <Box sx={{ ...TOOLBAR_SURFACE_SX, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {selectedElement && (
                  <>
                    <Tooltip title="Duplicera element (Ctrl+D)">
                      <IconButton size="small" onClick={() => duplicateElement(selectedElement.id)}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={selectedElement.locked ? 'Lås upp element (Ctrl+L)' : 'Lås element (Ctrl+L)'}>
                      <IconButton size="small" onClick={() => toggleElementLock(selectedElement.id)}>
                        {selectedElement.locked ? <LockIcon /> : <LockOpenIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Centrera horisontellt">
                      <IconButton size="small" onClick={() => centerElementHorizontally(selectedElement.id)} disabled={!!selectedElement.locked}>
                        <FormatAlignCenterIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Centrera vertikalt">
                      <IconButton size="small" onClick={() => centerElementVertically(selectedElement.id)} disabled={!!selectedElement.locked}>
                        <VerticalAlignCenterIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Zooma ut">
                  <IconButton size="small" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zooma in">
                  <IconButton size="small" onClick={() => setZoom(z => Math.min(2, z + 0.1))}>
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="outlined" onClick={() => setZoomToFit('page')}>
                  Passa sida
                </Button>
                <Button size="small" variant="outlined" onClick={() => setZoomToFit('width')}>
                  Passa bredd
                </Button>
                <Button size="small" variant="outlined" onClick={() => setZoomToFit('actual')}>
                  100%
                </Button>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Handverktyg (panorera)">
                  <IconButton
                    size="small"
                    color={panMode ? 'primary' : 'default'}
                    onClick={() => setPanMode((v) => !v)}
                  >
                    <PanToolIcon />
                  </IconButton>
                </Tooltip>

                <Button size="small" variant={showGrid ? 'contained' : 'outlined'} onClick={() => setShowGrid((v) => !v)}>
                  Rutnät
                </Button>

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={snapToGrid}
                      onChange={(e) => setSnapToGrid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Snap</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Rutnät</InputLabel>
                  <Select
                    value={gridSizeMm}
                    label="Rutnät"
                    onChange={(e) => setGridSizeMm(Number(e.target.value) || 10)}
                  >
                    <MenuItem value={1}>1 mm</MenuItem>
                    <MenuItem value={2}>2 mm</MenuItem>
                    <MenuItem value={5}>5 mm</MenuItem>
                    <MenuItem value={10}>10 mm</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Tips: håll Alt för att tillfälligt stänga av snap">
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    Alt=friare
                  </Typography>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Chip
                  size="small"
                  color={previewIsStale ? 'warning' : previewUrl ? 'success' : 'default'}
                  variant={previewUrl ? 'filled' : 'outlined'}
                  label={
                    previewIsStale
                      ? 'Preview ej uppdaterad'
                      : previewUrl
                        ? 'Preview aktuell'
                        : 'Ingen preview ännu'
                  }
                />

                <Tooltip title="Ångra (Ctrl+Z)">
                  <span>
                    <IconButton size="small" onClick={undo} disabled={!canUndo}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Gör om (Ctrl+Y)">
                  <span>
                    <IconButton size="small" onClick={redo} disabled={!canRedo}>
                      <RedoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', px: 0.25 }}>
                <Chip
                  size="small"
                  label={`A4 • ${currentTemplate.page.orientation === 'landscape' ? 'Liggande' : 'Stående'}`}
                  sx={{ bgcolor: 'rgba(27, 57, 103, 0.08)', color: FONTANA_COLORS.darkBlue }}
                />
                <Chip
                  size="small"
                  label={`Innehållsyta ${Math.round(contentWidthMm * 10) / 10} × ${Math.round(contentHeightMm * 10) / 10} mm`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={previewSelectionSummary}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={selectionSummary}
                  color={selectedElement ? 'primary' : selectedSlot ? 'warning' : 'default'}
                  variant={selectedElement || selectedSlot ? 'filled' : 'outlined'}
                />
              </Box>
            </>
          )}
        </Box>
        
        {/* Canvas Area */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1, overflow: 'hidden' }}>
          {workspaceViewMode !== 'preview' && (
            <Box
              ref={canvasScrollRef}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflow: 'auto',
                background: 'radial-gradient(circle at top, rgba(74,123,167,0.16), rgba(224,224,224,1) 38%)',
                borderRadius: 2.5,
              }}
              onMouseDown={handlePanMouseDown}
            >
              <Box
                sx={{
                  minWidth: pageWidthPx + 96,
                  minHeight: pageHeightPx + 96,
                  width: `max(${pageWidthPx + 96}px, 100%)`,
                  height: `max(${pageHeightPx + 96}px, 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 6,
                  boxSizing: 'border-box',
                }}
              >
              <Box
                ref={canvasRef}
                sx={{
                  width: pageWidthPx,
                  height: pageHeightPx,
                  backgroundColor: currentTemplate.page.backgroundColor || '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  position: 'relative',
                  cursor: panMode ? (isPanning ? 'grabbing' : 'grab') : (isDragging || isDraggingSlot ? 'grabbing' : 'default'),
                }}
                onMouseDown={handlePanMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onClick={() => {
                  setSelectedElementId(null);
                  setSelectedSlotId(null);
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: showGrid ? 'block' : 'none',
                    backgroundImage: `
                      linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)
                    `,
                    backgroundSize: `${gridSizeMm * MM_TO_PX_BASE * zoom}px ${gridSizeMm * MM_TO_PX_BASE * zoom}px`,
                    pointerEvents: 'none',
                  }}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    left: contentOffsetXPx,
                    top: contentOffsetYPx,
                    width: contentWidthPx,
                    height: contentHeightPx,
                    border: '1px dashed rgba(25, 118, 210, 0.35)',
                    backgroundColor: 'rgba(25, 118, 210, 0.02)',
                    pointerEvents: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      px: 1,
                      py: 0.4,
                      borderRadius: 999,
                      backgroundColor: 'rgba(255,255,255,0.92)',
                      color: FONTANA_COLORS.darkBlue,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    Innehållsyta
                  </Box>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    left: contentOffsetXPx,
                    top: contentOffsetYPx,
                    width: contentWidthPx,
                    height: contentHeightPx,
                    overflow: 'visible',
                  }}
                >
                  {snapGuides.verticalMm.map((guide) => (
                    <Box
                      key={`v-${guide}`}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: guide * MM_TO_PX_BASE * zoom,
                        width: 1,
                        backgroundColor: 'rgba(25, 118, 210, 0.55)',
                        pointerEvents: 'none',
                      }}
                    />
                  ))}
                  {snapGuides.horizontalMm.map((guide) => (
                    <Box
                      key={`h-${guide}`}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: guide * MM_TO_PX_BASE * zoom,
                        height: 1,
                        backgroundColor: 'rgba(25, 118, 210, 0.55)',
                        pointerEvents: 'none',
                      }}
                    />
                  ))}

                  {currentTemplate.templateMode === 'multi' && (
                    <>
                      {(currentTemplate.productSlots || []).map((slot, slotIdx) => {
                        const isSelectedSlot = slot.id === selectedSlotId;
                        const x = slot.xMm * MM_TO_PX_BASE * zoom;
                        const y = slot.yMm * MM_TO_PX_BASE * zoom;
                        const w = slot.wMm * MM_TO_PX_BASE * zoom;
                        const h = slot.hMm * MM_TO_PX_BASE * zoom;

                        const block = slot.blockTemplateId
                          ? blockTemplates.find((b) => b._id === slot.blockTemplateId) || null
                          : null;

                        const assignedAsset: IAsset | null = getAssignedMultiSlotAsset(multiPreviewAssets, slotIdx);

                        const blockWmm = block?.wMm || slot.wMm || 1;
                        const blockHmm = block?.hMm || slot.hMm || 1;
                        const innerWpx = blockWmm * MM_TO_PX_BASE * zoom;
                        const innerHpx = blockHmm * MM_TO_PX_BASE * zoom;
                        const scaleX = (slot.wMm || 1) / blockWmm;
                        const scaleY = (slot.hMm || 1) / blockHmm;

                        return (
                          <Box
                            key={slot.id}
                            sx={{
                              position: 'absolute',
                              left: x,
                              top: y,
                              width: w,
                              height: h,
                              border: isSelectedSlot ? '2px solid #ff9800' : '1px solid rgba(255,152,0,0.35)',
                              borderStyle: isSelectedSlot ? 'solid' : 'dashed',
                              borderRadius: 1,
                              bgcolor: 'rgba(255, 152, 0, 0.03)',
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                              cursor: 'grab',
                              zIndex: 1,
                              '&:hover': {
                                borderColor: 'rgba(255,152,0,0.75)',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (panMode) return;
                              setSelectedSlotId(slot.id);
                              setSelectedElementId(null);
                            }}
                            onMouseDown={(e) => handleSlotMouseDown(e, slot.id, 'drag')}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 6,
                                top: 6,
                                px: 0.7,
                                py: 0.3,
                                borderRadius: 1,
                                bgcolor: 'rgba(255,255,255,0.85)',
                                fontSize: 10,
                                color: '#555',
                                pointerEvents: 'none',
                              }}
                            >
                              {slotIdx + 1}
                              {block ? ` • ${block.name}` : ' • (välj block)'}
                              {!assignedAsset ? ' • (ingen produkt)' : ''}
                            </Box>

                            {block && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  width: innerWpx,
                                  height: innerHpx,
                                  transform: `scale(${scaleX}, ${scaleY})`,
                                  transformOrigin: 'top left',
                                  pointerEvents: 'none',
                                }}
                              >
                                {[...block.elements]
                                  .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                                  .map((be) => {
                                    const style = be.style || {};
                                    const bx = be.xMm * MM_TO_PX_BASE * zoom;
                                    const by = be.yMm * MM_TO_PX_BASE * zoom;
                                    const bw = be.wMm * MM_TO_PX_BASE * zoom;
                                    const bh = be.hMm * MM_TO_PX_BASE * zoom;
                                    const fontSizePx = (style.fontSize || 12) * PT_TO_PX * zoom;

                                    return (
                                      <Box
                                        key={be.id}
                                        sx={{
                                          position: 'absolute',
                                          left: bx,
                                          top: by,
                                          width: bw,
                                          height: bh,
                                          overflow: 'hidden',
                                          boxSizing: 'border-box',
                                          bgcolor: style.backgroundColor || 'transparent',
                                          fontSize: fontSizePx,
                                          fontFamily:
                                            style.fontFamily === 'Kapra Neue'
                                              ? "'Kapra Neue', 'Oswald', sans-serif"
                                              : style.fontFamily || 'Arial, sans-serif',
                                          fontWeight: style.fontWeight || 'normal',
                                          fontStyle: style.fontStyle || 'normal',
                                          color: style.color || '#000',
                                          textAlign: style.textAlign || 'left',
                                          padding: (style.padding || 0) * MM_TO_PX_BASE * zoom,
                                          borderRadius: (style.borderRadius || 0) * MM_TO_PX_BASE * zoom,
                                          textTransform: style.textTransform || 'none',
                                          letterSpacing: style.letterSpacing ? `${style.letterSpacing * PT_TO_PX * zoom}px` : undefined,
                                          lineHeight: style.lineHeight || 'normal',
                                          opacity: style.opacity !== undefined ? style.opacity : 1,
                                          transform: be.rotation ? `rotate(${be.rotation}deg)` : undefined,
                                          transformOrigin: 'center center',
                                        }}
                                      >
                                        {be.kind === 'logo' && (
                                          <img
                                            src="/Fontana_Logo_Neg.png"
                                            alt="Logo"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                          />
                                        )}

                                        {be.kind === 'productImage' && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              height: '100%',
                                              display: 'flex',
                                              alignItems:
                                                style.imageAlign === 'top'
                                                  ? 'flex-start'
                                                  : style.imageAlign === 'bottom'
                                                    ? 'flex-end'
                                                    : 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            {assignedAsset ? (
                                              <img
                                                src={withImgToken(`/api/assets/${assignedAsset._id}/preview`)}
                                                alt={assignedAsset.title || 'Produktbild'}
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                onError={(e) => {
                                                  const img = e.target as HTMLImageElement;
                                                  if (!img.src.includes('/preview/thumbnail')) {
                                                    img.src = withImgToken(`/api/assets/${assignedAsset._id}/preview/thumbnail`);
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <Box sx={{ color: '#999', textAlign: 'center' }}>
                                                <ImageIcon sx={{ fontSize: 18 * zoom }} />
                                              </Box>
                                            )}
                                          </Box>
                                        )}

                                        {be.kind === 'textField' && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              height: '100%',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent:
                                                style.verticalAlign === 'middle'
                                                  ? 'center'
                                                  : style.verticalAlign === 'bottom'
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                            }}
                                          >
                                            {(() => {
                                              const prefix = be.binding?.prefix || '';
                                              const suffix = be.binding?.suffix || '';
                                              const key = be.binding?.metadataKey || '';
                                              const value = getAssetMetadataValue(
                                                assignedAsset,
                                                key,
                                                be.binding?.fallbackValue || `{${key || 'fält'}}`
                                              );
                                              const text = `${prefix}${value}${suffix}`;
                                              return (
                                                <AutoFitText
                                                  text={text}
                                                  enabled={!!style.autoFit}
                                                  minFontSizePt={style.minFontSize}
                                                  depsKey={`${be.id}-${zoom}-${be.wMm}-${be.hMm}-${style.fontSize}-${style.lineHeight}-${style.letterSpacing}-${text}`}
                                                />
                                              );
                                            })()}
                                          </Box>
                                        )}

                                        {be.kind === 'staticText' && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              height: '100%',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent:
                                                style.verticalAlign === 'middle'
                                                  ? 'center'
                                                  : style.verticalAlign === 'bottom'
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                            }}
                                          >
                                            <AutoFitText
                                              text={be.content || 'Text...'}
                                              enabled={!!style.autoFit}
                                              minFontSizePt={style.minFontSize}
                                              depsKey={`${be.id}-${zoom}-${be.wMm}-${be.hMm}-${style.fontSize}-${style.lineHeight}-${style.letterSpacing}-${be.content || ''}`}
                                            />
                                          </Box>
                                        )}

                                        {be.kind === 'richText' && (
                                          <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                                            <Box
                                              sx={{ width: '100%', overflow: 'hidden' }}
                                              dangerouslySetInnerHTML={{ __html: renderRichTextHtml(be.content || '', assignedAsset) }}
                                            />
                                          </Box>
                                        )}

                                        {be.kind === 'line' && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              height: 0,
                                              borderTop: `${(style.borderWidth || 1) * MM_TO_PX_BASE * zoom}px ${style.borderStyle || 'solid'} ${style.borderColor || '#000'}`,
                                            }}
                                          />
                                        )}

                                        {be.kind === 'staticImage' && (
                                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {be.imageUrl ? (
                                              <img
                                                src={withImgToken(
                                                  be.imageUrl.includes('/api/assets/') && be.imageUrl.includes('/download')
                                                    ? be.imageUrl.replace('/download', '/preview')
                                                    : be.imageUrl
                                                )}
                                                alt="Bild"
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                              />
                                            ) : null}
                                          </Box>
                                        )}

                                        {be.kind === 'bulletList' && (
                                          <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                                            <ul
                                              style={{
                                                margin: 0,
                                                paddingLeft: '1.5em',
                                                listStyleType: style.bulletStyle || 'disc',
                                                fontSize: 'inherit',
                                                lineHeight: 'inherit',
                                              }}
                                            >
                                              {(be.content || '').split('\n').map((line, i) => {
                                                const cleanLine = line.replace(/^[•◦▪■○●*-]\s*/, '').trim();
                                                return cleanLine ? <li key={i}>{cleanLine}</li> : null;
                                              })}
                                            </ul>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                              </Box>
                            )}

                            {isSelectedSlot && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -4,
                                  bottom: -4,
                                  width: 10,
                                  height: 10,
                                  backgroundColor: '#ff9800',
                                  cursor: 'se-resize',
                                }}
                                onMouseDown={(e) => handleSlotMouseDown(e, slot.id, 'resize')}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </>
                  )}

                  {sortedElements.map(element => {
                    const isSelected = element.id === selectedElementId;
                    const x = element.xMm * MM_TO_PX_BASE * zoom;
                    const y = element.yMm * MM_TO_PX_BASE * zoom;
                    const w = element.wMm * MM_TO_PX_BASE * zoom;
                    const h = element.hMm * MM_TO_PX_BASE * zoom;
                    const showOutline = isSelected || !pagePreviewAsset;
                    const fontSizePx = (element.style.fontSize || 12) * PT_TO_PX * zoom;

                    return (
                      <Box
                        key={element.id}
                        sx={{
                          position: 'absolute',
                          left: x,
                          top: y,
                          width: w,
                          height: h,
                          zIndex: element.zIndex ?? 0,
                          border: isSelected
                            ? '2px solid #1976d2'
                            : showOutline
                              ? '1px dashed rgba(0,0,0,0.2)'
                              : '1px solid transparent',
                          backgroundColor: element.style.backgroundColor || 'transparent',
                          cursor: element.locked ? 'not-allowed' : 'grab',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          fontSize: fontSizePx,
                          fontFamily: element.style.fontFamily === 'Kapra Neue'
                            ? "'Kapra Neue', 'Oswald', sans-serif"
                            : element.style.fontFamily || 'Arial, sans-serif',
                          fontWeight: element.style.fontWeight || 'normal',
                          fontStyle: element.style.fontStyle || 'normal',
                          color: element.kind === 'textField' && !pagePreviewAsset ? '#666' : (element.style.color || '#000'),
                          textAlign: element.style.textAlign || 'left',
                          padding: (element.style.padding || 0) * MM_TO_PX_BASE * zoom,
                          borderRadius: (element.style.borderRadius || 0) * MM_TO_PX_BASE * zoom,
                          textTransform: element.style.textTransform || 'none',
                          letterSpacing: element.style.letterSpacing ? `${element.style.letterSpacing * PT_TO_PX * zoom}px` : undefined,
                          lineHeight: element.style.lineHeight || 'normal',
                          opacity: element.style.opacity !== undefined ? element.style.opacity : 1,
                          transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
                          transformOrigin: 'center center',
                          transition: 'border-color 0.15s ease',
                          '&:hover': {
                            borderColor: isSelected ? '#1976d2' : 'rgba(25, 118, 210, 0.5)',
                            border: isSelected ? '2px solid #1976d2' : '1px dashed rgba(25, 118, 210, 0.5)',
                          },
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          if (panMode) return;
                          setSelectedElementId(element.id);
                          setSelectedSlotId(null);
                        }}
                        onMouseDown={e => handleCanvasMouseDown(e, element.id, 'drag')}
                      >
                        {element.locked && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: 'rgba(255,255,255,0.85)',
                              color: '#555',
                              pointerEvents: 'none',
                            }}
                          >
                            <LockIcon sx={{ fontSize: 12 * zoom }} />
                            <Typography variant="caption" sx={{ fontSize: 8 * zoom }}>
                              Låst
                            </Typography>
                          </Box>
                        )}
                        {element.kind === 'logo' && (
                          <img
                            src="/Fontana_Logo_Neg.png"
                            alt="Fontana Logo"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                            }}
                          />
                        )}
                        {element.kind === 'productImage' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: element.style.imageAlign === 'top' ? 'flex-start' : element.style.imageAlign === 'bottom' ? 'flex-end' : 'center',
                            justifyContent: 'center',
                          }}>
                            {pagePreviewAsset ? (
                              <img
                                src={withImgToken(`/api/assets/${pagePreviewAsset._id}/preview`)}
                                alt={pagePreviewAsset.title || 'Produktbild'}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  if (!img.src.includes('/preview/thumbnail')) {
                                    img.src = withImgToken(`/api/assets/${pagePreviewAsset._id}/preview/thumbnail`);
                                  }
                                }}
                              />
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 0.5,
                                color: '#999',
                              }}>
                                <ImageIcon sx={{ fontSize: 24 * zoom }} />
                                <Typography variant="caption" sx={{ fontSize: 8 * zoom }}>
                                  Välj produkt
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        {element.kind === 'textField' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: element.style.verticalAlign === 'middle' ? 'center' :
                                           element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          }}>
                            {(() => {
                              const prefix = element.binding?.prefix || '';
                              const suffix = element.binding?.suffix || '';
                              const value = getAssetMetadataValue(
                                pagePreviewAsset,
                                element.binding?.metadataKey || '',
                                element.binding?.fallbackValue || `{${element.binding?.metadataKey || 'fält'}}`
                              );
                              const text = `${prefix}${value}${suffix}`;
                              return (
                                <AutoFitText
                                  text={text}
                                  enabled={!!element.style.autoFit}
                                  minFontSizePt={element.style.minFontSize}
                                  depsKey={`${element.id}-${zoom}-${element.wMm}-${element.hMm}-${element.style.fontSize}-${element.style.lineHeight}-${element.style.letterSpacing}-${text}`}
                                />
                              );
                            })()}
                          </Box>
                        )}
                        {element.kind === 'staticText' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: element.style.verticalAlign === 'middle' ? 'center' :
                                           element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          }}>
                            <AutoFitText
                              text={element.content || 'Text...'}
                              enabled={!!element.style.autoFit}
                              minFontSizePt={element.style.minFontSize}
                              depsKey={`${element.id}-${zoom}-${element.wMm}-${element.hMm}-${element.style.fontSize}-${element.style.lineHeight}-${element.style.letterSpacing}-${element.content || ''}`}
                            />
                          </Box>
                        )}
                        {element.kind === 'richText' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: element.style.verticalAlign === 'middle' ? 'center' :
                                           element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          }}>
                            <Box
                              sx={{ width: '100%', overflow: 'hidden' }}
                              dangerouslySetInnerHTML={{ __html: renderRichTextHtml(element.content || '', pagePreviewAsset) }}
                            />
                          </Box>
                        )}
                        {element.kind === 'line' && (
                          <Box sx={{
                            width: '100%',
                            height: 0,
                            borderTop: `${(element.style.borderWidth || 1) * MM_TO_PX_BASE * zoom}px ${element.style.borderStyle || 'solid'} ${element.style.borderColor || '#000'}`,
                          }} />
                        )}
                        {element.kind === 'staticImage' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {element.imageUrl ? (
                              <img
                                src={withImgToken(
                                  element.imageUrl.includes('/api/assets/') && element.imageUrl.includes('/download')
                                    ? element.imageUrl.replace('/download', '/preview')
                                    : element.imageUrl
                                )}
                                alt="Header/Banner"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  if (element.imageUrl?.includes('/api/assets/') && !img.src.includes('/preview/thumbnail')) {
                                    img.src = withImgToken(
                                      element.imageUrl
                                        .replace('/download', '/preview/thumbnail')
                                        .replace(/\/preview(\?|$)/, '/preview/thumbnail$1')
                                    );
                                  } else {
                                    img.style.display = 'none';
                                  }
                                }}
                              />
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 0.5,
                                color: '#4caf50',
                              }}>
                                <PhotoLibraryIcon sx={{ fontSize: 20 * zoom }} />
                                <Typography variant="caption" sx={{ fontSize: 8 * zoom }}>
                                  Välj bild
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        {element.kind === 'bulletList' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: element.style.verticalAlign === 'middle' ? 'center' :
                                           element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          }}>
                            <ul style={{
                              margin: 0,
                              paddingLeft: '1.5em',
                              listStyleType: element.style.bulletStyle || 'disc',
                              fontSize: 'inherit',
                              lineHeight: 'inherit',
                            }}>
                              {(element.content || '• Punkt').split('\n').map((line, i) => {
                                const cleanLine = line.replace(/^[•◦▪■○●*-]\s*/, '').trim();
                                return cleanLine ? (
                                  <li key={i} style={{ fontSize: 'inherit' }}>
                                    {cleanLine}
                                  </li>
                                ) : null;
                              })}
                            </ul>
                          </Box>
                        )}

                        {isSelected && !element.locked && (
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -4,
                              bottom: -4,
                              width: 8,
                              height: 8,
                              backgroundColor: '#1976d2',
                              cursor: 'se-resize',
                            }}
                            onMouseDown={e => handleCanvasMouseDown(e, element.id, 'resize')}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              </Box>
            </Box>
          )}

          {workspaceViewMode !== 'design' && (
            <Paper
              variant="outlined"
              sx={{
                ...PANEL_SX,
                width: workspaceViewMode === 'split' ? 520 : undefined,
                minWidth: workspaceViewMode === 'split' ? 360 : 0,
                flex: workspaceViewMode === 'preview' ? 1 : '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: workspaceViewMode === 'split' ? 110 : 170, flex: '0 0 auto' }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.15 }}>PDF-preview</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: workspaceViewMode === 'split' ? 'none' : 'block', lineHeight: 1.1 }}
                    >
                      {currentTemplate.templateMode === 'multi'
                        ? 'Lägg till produkter och generera.'
                        : 'Välj produkt och generera.'}
                    </Typography>
                  </Box>

                  <Autocomplete
                    size="small"
                    options={assetOptions}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => option.title || option.fileName || ''}
                    inputValue={assetSearch}
                    onInputChange={(_, value) => setAssetSearch(value)}
                    onChange={(_, asset) => {
                      if (!asset) return;
                      if (currentTemplate.templateMode === 'multi') {
                        addMultiPreviewAsset(asset._id);
                      } else {
                        setPreviewAssetId(asset._id);
                      }
                      setAssetSearch('');
                    }}
                    sx={{
                      flex: '1 1 300px',
                      minWidth: workspaceViewMode === 'split' ? 180 : 300,
                      '& .MuiInputBase-root': { height: 34 },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={currentTemplate.templateMode === 'multi' ? 'Lägg till produkt' : 'Sök produkt'}
                        placeholder="Titel eller filnamn..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={withImgToken(`/api/assets/${option._id}/preview/thumbnail`)}
                            alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8, backgroundColor: '#eef3f9' }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          <Box>
                            <Typography variant="body2">{option.title || option.fileName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[option.fileName, getAssetArticleNumbers(option)[0] ? `Art.nr ${getAssetArticleNumbers(option)[0]}` : '']
                                .filter(Boolean)
                                .join(' • ')}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                  />

                  <Chip
                    size="small"
                    label={autoPreviewEnabled ? 'Auto-preview' : 'Manuell preview'}
                    sx={{ bgcolor: autoPreviewEnabled ? 'rgba(74, 123, 167, 0.14)' : 'rgba(27, 57, 103, 0.08)', color: FONTANA_COLORS.darkBlue }}
                  />
                  {currentTemplate.templateMode === 'multi' ? (
                    <Chip
                      size="small"
                      label={`${multiPreviewAssets.length}/${currentTemplate.maxProducts || currentTemplate.productSlots?.length || 0} produkter`}
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      size="small"
                      label={previewSelectionSummary}
                      color={previewAsset ? 'primary' : 'default'}
                      variant="outlined"
                      onDelete={previewAsset ? () => {
                        setPreviewAssetId('');
                        setPreviewAsset(null);
                        clearPreview();
                      } : undefined}
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={autoPreviewEnabled}
                        onChange={(e) => setAutoPreviewEnabled(e.target.checked)}
                      />
                    }
                    label="Auto-preview"
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                  />
                  <Button
                    size="small"
                    variant={previewIsStale || !previewUrl ? 'contained' : 'outlined'}
                    startIcon={previewLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={generatePreview}
                    disabled={!hasPreviewSource || previewLoading}
                    sx={{ minHeight: 32 }}
                  >
                    {previewLoading ? 'Genererar...' : previewIsStale ? 'Uppdatera' : 'Generera'}
                  </Button>
                  {previewUrl && (
                    <Tooltip title="Öppna i fullskärm">
                      <IconButton size="small" onClick={() => setPreviewDialogOpen(true)} color="primary">
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {previewUrl && (
                    <Tooltip title="Ladda ner aktuell preview-PDF">
                      <IconButton size="small" onClick={handleDownloadPreview} color="primary">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {(previewIsStale || previewError) && (
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                    {previewIsStale && (
                      <Alert severity="warning" sx={{ py: 0, alignItems: 'center' }}>Previewn behöver uppdateras.</Alert>
                    )}
                    {previewError && (
                      <Alert severity="error" sx={{ py: 0, alignItems: 'center' }}>{previewError}</Alert>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                  {currentTemplate.templateMode === 'multi' && multiPreviewAssets.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {multiPreviewAssets.map((asset, index) => (
                          <Chip
                            key={asset._id}
                            label={`${index + 1}. ${asset.title || asset.fileName}`}
                            size="small"
                            variant="outlined"
                            onDelete={() => removeMultiPreviewAsset(asset._id)}
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" color="inherit" onClick={clearAllPreviewAssets}>
                          Rensa previewprodukter
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {previewUrl ? (
                <Box sx={{ flex: 1, minHeight: 280, p: 0.75 }}>
                  <iframe
                    src={previewUrl}
                    style={{ width: '100%', height: '100%', border: '1px solid rgba(27,57,103,0.08)', borderRadius: '16px', background: '#fff' }}
                    title="PDF Preview"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'radial-gradient(circle at top, rgba(74,123,167,0.12), rgba(248,249,251,1) 58%)',
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    {currentTemplate.templateMode === 'multi'
                      ? hasPreviewSource
                        ? 'Klicka på "Generera" för att se PDF-previewen.'
                        : 'Lägg till produkter för att bygga en multi-preview.'
                      : previewAssetId
                        ? 'Klicka på "Generera" för att se aktuell PDF-preview.'
                        : 'Välj en produkt för att starta previewn.'}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Paper>
      
      {/* Right Panel - Properties */}
      {workspaceViewMode !== 'preview' && (
      <Paper
        sx={{
          ...PANEL_SX,
          width: rightPanelWidth,
          minWidth: 280,
          maxWidth: 720,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Drag handle to resize panel */}
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            rightPanelResizeRef.current = { startX: e.clientX, startWidth: rightPanelWidth };
            setIsResizingRightPanel(true);
          }}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            zIndex: 20,
            '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
          }}
        />
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <PdfPanelRail activePanel={activePanel} onChange={setActivePanel} />
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedElement && !selectedElementPanelMatches && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={<Button size="small" onClick={() => setActivePanel('elements')}>Egenskaper</Button>}
          >
            Markerat: {ELEMENT_KIND_LABELS[selectedElement.kind]}
          </Alert>
        )}
        {selectedSlot && activePanel !== 'tools' && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={<Button size="small" onClick={() => setActivePanel('tools')}>Öppna</Button>}
          >
            Produktplats markerad
          </Alert>
        )}
        {selectedElement && selectedElementPanelMatches ? (
          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              '& .MuiAccordion-root': {
                mb: 1.25,
                border: '1px solid rgba(27, 57, 103, 0.08)',
                borderRadius: '14px !important',
                overflow: 'hidden',
                boxShadow: 'none',
                backgroundColor: 'rgba(255,255,255,0.75)',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: 48,
                backgroundColor: 'rgba(27, 57, 103, 0.04)',
              },
            }}
          >
            {/* Element Type */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25, p: 1.25, borderRadius: 2, bgcolor: 'rgba(27, 57, 103, 0.05)' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {ELEMENT_KIND_LABELS[selectedElement.kind]}
              </Typography>
              <Chip
                size="small"
                icon={selectedElement.locked ? <LockIcon /> : <LockOpenIcon />}
                label={selectedElement.locked ? 'Låst' : 'Olåst'}
                color={selectedElement.locked ? 'warning' : 'default'}
                onClick={() => toggleElementLock(selectedElement.id)}
                clickable
                variant={selectedElement.locked ? 'filled' : 'outlined'}
              />
            </Box>
            
            <Accordion defaultExpanded key={`pos-${selectedElement.id}`} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Position & Storlek</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary">Position i innehållsyta & storlek (mm)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="X"
                    type="number"
                    value={selectedElement.xMm}
                    onChange={e => updateElement(selectedElement.id, { xMm: parseFloat(e.target.value) || 0 })}
                    disabled={!!selectedElement.locked}
                  />
                  <TextField
                    size="small"
                    label="Y"
                    type="number"
                    value={selectedElement.yMm}
                    onChange={e => updateElement(selectedElement.id, { yMm: parseFloat(e.target.value) || 0 })}
                    disabled={!!selectedElement.locked}
                  />
                  <TextField
                    size="small"
                    label="Bredd"
                    type="number"
                    value={selectedElement.wMm}
                    onChange={e => updateElement(selectedElement.id, { wMm: parseFloat(e.target.value) || 10 })}
                    disabled={!!selectedElement.locked}
                  />
                  <TextField
                    size="small"
                    label="Höjd"
                    type="number"
                    value={selectedElement.hMm}
                    onChange={e => updateElement(selectedElement.id, { hMm: parseFloat(e.target.value) || 10 })}
                    disabled={!!selectedElement.locked}
                  />
                </Box>

                {selectedElement.kind === 'productImage' && (
                  <>
                    <Typography variant="caption" color="text.secondary">Bildjustering</Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Vertikal</InputLabel>
                      <Select
                        value={selectedElement.style.imageAlign || 'center'}
                        label="Vertikal"
                        onChange={e => updateElementStyle(selectedElement.id, { imageAlign: e.target.value as 'top' | 'center' | 'bottom' })}
                      >
                        <MenuItem value="top">Topp</MenuItem>
                        <MenuItem value="center">Centrerad</MenuItem>
                        <MenuItem value="bottom">Botten</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            {(selectedElement.kind === 'textField' || selectedElement.kind === 'staticText' || selectedElement.kind === 'richText') && (
              <Accordion defaultExpanded key={`content-${selectedElement.id}`} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Innehåll</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedElement.kind === 'textField' && (
                    <>
                      <Typography variant="caption" color="text.secondary">Databindning</Typography>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Fält</InputLabel>
                        <Select
                          value={selectedElement.binding?.metadataKey || ''}
                          label="Fält"
                          onChange={e => updateElement(selectedElement.id, {
                            binding: { ...selectedElement.binding, metadataKey: e.target.value as string },
                          })}
                        >
                          {availableFields.map(field => (
                            <MenuItem key={field.id} value={field.id}>
                              {field.label} ({field.templateName})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Prefix"
                        fullWidth
                        value={selectedElement.binding?.prefix || ''}
                        onChange={e => updateElement(selectedElement.id, {
                          binding: { ...selectedElement.binding!, prefix: e.target.value },
                        })}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Suffix"
                        fullWidth
                        value={selectedElement.binding?.suffix || ''}
                        onChange={e => updateElement(selectedElement.id, {
                          binding: { ...selectedElement.binding!, suffix: e.target.value },
                        })}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Standardvärde"
                        fullWidth
                        value={selectedElement.binding?.fallbackValue || ''}
                        onChange={e => updateElement(selectedElement.id, {
                          binding: { ...selectedElement.binding!, fallbackValue: e.target.value },
                        })}
                        sx={{ mb: 2 }}
                      />
                    </>
                  )}

                  {selectedElement.kind === 'staticText' && (
                    <>
                      <Typography variant="caption" color="text.secondary">Innehåll</Typography>
                      <TextField
                        size="small"
                        label="Text"
                        fullWidth
                        multiline
                        rows={3}
                        value={selectedElement.content || ''}
                        onChange={e => updateElement(selectedElement.id, { content: e.target.value })}
                        sx={{ mb: 2 }}
                      />
                    </>
                  )}

                  {selectedElement.kind === 'richText' && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Textblock (rik formatering)</Typography>
                        <ToggleButtonGroup
                          size="small"
                          value={richTextEditorMode}
                          exclusive
                          onChange={(_, value) => value && setRichTextEditorMode(value)}
                        >
                          <ToggleButton value="design">Design</ToggleButton>
                          <ToggleButton value="code">HTML</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      {richTextEditorMode === 'design' ? (
                        <>
                          <RichTextWysiwygEditor
                            value={selectedElement.content || ''}
                            onChange={(html) => updateElement(selectedElement.id, { content: html })}
                            availableFields={availableFields}
                            baseStyle={selectedElement.style}
                          />
                          <Accordion disableGutters sx={{ mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="caption">Kodläge (HTML)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Alert severity="info" sx={{ mb: 1 }}>
                                Vanlig HTML med inline-styles fungerar bäst. Enkla `style`-block med klassnamn försöker konverteras automatiskt, men externa fonter, script, pseudo-element och avancerad CSS stöds inte fullt.
                              </Alert>
                              <TextField
                                size="small"
                                label="Innehåll (HTML)"
                                fullWidth
                                multiline
                                rows={6}
                                value={selectedElement.content || ''}
                                onChange={e => updateElement(selectedElement.id, { content: e.target.value })}
                                placeholder={`Ex:\n<span style="font-family:'Kapra Neue'; font-size:18pt; color:#1B3967;">{{title}}</span><br/>\n<span style="font-size:10pt; color:#333;">Art.nr: {{artikelnummer}}</span>`}
                                helperText="Stöd för {{metadataKey}} och även {metadataKey}. Klistra helst in innehålls-HTML, inte hela webblayouter."
                              />
                            </AccordionDetails>
                          </Accordion>
                        </>
                      ) : (
                        <>
                          <Alert severity="info" sx={{ mb: 1 }}>
                            Kodläge är bäst för semantiskt innehåll och inline-styles. Externa font-länkar, script, pseudo-element som `::after` och komplex webblayout-CSS matchar inte fullt i canvas/PDF.
                          </Alert>
                          <TextField
                            size="small"
                            label="Innehåll (HTML)"
                            fullWidth
                            multiline
                            rows={12}
                            value={selectedElement.content || ''}
                            onChange={e => updateElement(selectedElement.id, { content: e.target.value })}
                            sx={{ mb: 1 }}
                            placeholder={`Ex:\n<span style="font-family:'Kapra Neue'; font-size:18pt; color:#1B3967;">{{title}}</span><br/>\n<span style="font-size:10pt; color:#333;">Art.nr: {{artikelnummer}}</span>`}
                            helperText="Kodläge för exakt HTML-redigering. Placeholders som {{metadataKey}} eller {metadataKey} fungerar fortfarande."
                          />
                        </>
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {(selectedElement.kind === 'textField' || selectedElement.kind === 'staticText' || selectedElement.kind === 'richText') && (
              <Accordion defaultExpanded={!isRichTextElement} key={`typo-${selectedElement.id}`} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Typografi</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Typsnitt</InputLabel>
                    <Select
                      value={selectedElement.style.fontFamily || 'Arial'}
                      label="Typsnitt"
                      onChange={e => updateElementStyle(selectedElement.id, { fontFamily: e.target.value as any })}
                    >
                      {FONT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Storlek (pt)"
                    type="number"
                    fullWidth
                    value={selectedElement.style.fontSize || 12}
                    onChange={e => updateElementStyle(selectedElement.id, { fontSize: parseInt(e.target.value) || 12 })}
                    sx={{ mb: 1 }}
                  />

                  {(selectedElement.kind === 'textField' || selectedElement.kind === 'staticText') && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!selectedElement.style.autoFit}
                            onChange={e => updateElementStyle(selectedElement.id, { autoFit: e.target.checked })}
                          />
                        }
                        label="Auto-anpassa text till rutan"
                        sx={{ mb: 1 }}
                      />
                      {selectedElement.style.autoFit && (
                        <TextField
                          size="small"
                          label="Minsta storlek (pt)"
                          type="number"
                          fullWidth
                          value={selectedElement.style.minFontSize || 6}
                          onChange={e => updateElementStyle(selectedElement.id, { minFontSize: parseFloat(e.target.value) || 6 })}
                          sx={{ mb: 1 }}
                        />
                      )}
                    </>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Vikt</InputLabel>
                      <Select
                        value={selectedElement.style.fontWeight || 'normal'}
                        label="Vikt"
                        onChange={e => updateElementStyle(selectedElement.id, { fontWeight: e.target.value as any })}
                      >
                        {FONT_WEIGHT_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <ToggleButtonGroup
                      value={[
                        selectedElement.style.fontWeight === 'bold' ? 'bold' : null,
                        selectedElement.style.fontStyle === 'italic' ? 'italic' : null,
                      ].filter(Boolean)}
                      onChange={(_, values) => {
                        updateElementStyle(selectedElement.id, { 
                          fontWeight: values.includes('bold') ? 'bold' : 'normal',
                          fontStyle: values.includes('italic') ? 'italic' : 'normal',
                        });
                      }}
                      size="small"
                    >
                      <ToggleButton value="bold"><FormatBoldIcon fontSize="small" /></ToggleButton>
                      <ToggleButton value="italic"><FormatItalicIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <ToggleButtonGroup
                      value={selectedElement.style.textAlign || 'left'}
                      exclusive
                      onChange={(_, v) => v && updateElementStyle(selectedElement.id, { textAlign: v })}
                      size="small"
                    >
                      <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
                      <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
                      <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                      value={selectedElement.style.verticalAlign || 'top'}
                      exclusive
                      onChange={(_, v) => v && updateElementStyle(selectedElement.id, { verticalAlign: v })}
                      size="small"
                    >
                      <ToggleButton value="top"><VerticalAlignTopIcon fontSize="small" /></ToggleButton>
                      <ToggleButton value="middle"><VerticalAlignCenterIcon fontSize="small" /></ToggleButton>
                      <ToggleButton value="bottom"><VerticalAlignBottomIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel>Transform</InputLabel>
                    <Select
                      value={selectedElement.style.textTransform || 'none'}
                      label="Transform"
                      onChange={e => updateElementStyle(selectedElement.id, { textTransform: e.target.value as any })}
                    >
                      {TEXT_TRANSFORM_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      label="Radavstånd"
                      type="number"
                      value={selectedElement.style.lineHeight || 1.4}
                      onChange={e => updateElementStyle(selectedElement.id, { lineHeight: parseFloat(e.target.value) || 1.4 })}
                      inputProps={{ step: 0.1, min: 0.8 }}
                    />
                    <TextField
                      size="small"
                      label="Teckenmellanrum (pt)"
                      type="number"
                      value={selectedElement.style.letterSpacing || 0}
                      onChange={e => updateElementStyle(selectedElement.id, { letterSpacing: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 0.1 }}
                    />
                    <TextField
                      size="small"
                      label="Innerpadding (mm)"
                      type="number"
                      value={selectedElement.style.padding || 0}
                      onChange={e => updateElementStyle(selectedElement.id, { padding: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 0.5, min: 0 }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion defaultExpanded={!isRichTextElement} key={`appearance-${selectedElement.id}`} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Utseende</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary">Färger</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 60 }}>Text:</Typography>
                  <input
                    type="color"
                    value={selectedElement.style.color || '#000000'}
                    onChange={e => updateElementStyle(selectedElement.id, { color: e.target.value })}
                    style={{ width: 40, height: 30, border: 'none', cursor: 'pointer' }}
                  />
                  <TextField
                    size="small"
                    value={selectedElement.style.color || '#000000'}
                    onChange={e => updateElementStyle(selectedElement.id, { color: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                  {Object.entries(FONTANA_COLORS).map(([label, color]) => (
                    <Tooltip key={`text-${label}`} title={`Sätt textfärg: ${label}`}>
                      <Box
                        onClick={() => updateElementStyle(selectedElement.id, { color })}
                        sx={{
                          ...COLOR_SWATCH_BUTTON_SX,
                          backgroundColor: color,
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 60 }}>Bakgrund:</Typography>
                  <input
                    type="color"
                    value={selectedElement.style.backgroundColor || '#ffffff'}
                    onChange={e => updateElementStyle(selectedElement.id, { backgroundColor: e.target.value })}
                    style={{ width: 40, height: 30, border: 'none', cursor: 'pointer' }}
                  />
                  <TextField
                    size="small"
                    value={selectedElement.style.backgroundColor || ''}
                    onChange={e => updateElementStyle(selectedElement.id, { backgroundColor: e.target.value })}
                    sx={{ flex: 1 }}
                    placeholder="transparent"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                  {Object.entries(FONTANA_COLORS).map(([label, color]) => (
                    <Tooltip key={`bg-${label}`} title={`Sätt bakgrund: ${label}`}>
                      <Box
                        onClick={() => updateElementStyle(selectedElement.id, { backgroundColor: color })}
                        sx={{
                          ...COLOR_SWATCH_BUTTON_SX,
                          backgroundColor: color,
                        }}
                      />
                    </Tooltip>
                  ))}
                  <Tooltip title="Gör bakgrunden transparent">
                    <Box
                      onClick={() => updateElementStyle(selectedElement.id, { backgroundColor: '' })}
                      sx={{
                        ...COLOR_SWATCH_BUTTON_SX,
                        background: 'linear-gradient(135deg, #ffffff 0%, #ffffff 45%, #ff6b6b 45%, #ff6b6b 55%, #ffffff 55%)',
                      }}
                    />
                  </Tooltip>
                </Box>

                {(selectedElement.kind === 'rectangle' || selectedElement.kind === 'line') && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">Ram</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        label="Tjocklek (mm)"
                        type="number"
                        value={selectedElement.style.borderWidth || 0}
                        onChange={e => updateElementStyle(selectedElement.id, { borderWidth: parseFloat(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: 0.25 }}
                        sx={{ flex: 1 }}
                      />
                      <input
                        type="color"
                        value={selectedElement.style.borderColor || '#000000'}
                        onChange={e => updateElementStyle(selectedElement.id, { borderColor: e.target.value })}
                        style={{ width: 40, height: 40, border: 'none', cursor: 'pointer' }}
                      />
                    </Box>
                    {selectedElement.kind === 'rectangle' && (
                      <TextField
                        size="small"
                        label="Rundade hörn (mm)"
                        type="number"
                        fullWidth
                        value={selectedElement.style.borderRadius || 0}
                        onChange={e => updateElementStyle(selectedElement.id, { borderRadius: parseFloat(e.target.value) || 0 })}
                        sx={{ mb: 2 }}
                      />
                    )}
                    {selectedElement.kind === 'line' && (
                      <>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Linjestil</InputLabel>
                          <Select
                            value={selectedElement.style.borderStyle || 'solid'}
                            label="Linjestil"
                            onChange={e => updateElementStyle(selectedElement.id, { borderStyle: e.target.value as 'solid' | 'dashed' | 'dotted' })}
                          >
                            {BORDER_STYLE_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label="Rotation (°)"
                            type="number"
                            value={selectedElement.rotation || 0}
                            onChange={e => updateElement(selectedElement.id, { rotation: parseFloat(e.target.value) || 0 })}
                            sx={{ flex: 1 }}
                            inputProps={{ step: 15 }}
                          />
                          <Tooltip title="Rotera 90°">
                            <IconButton 
                              size="small" 
                              onClick={() => updateElement(selectedElement.id, { rotation: ((selectedElement.rotation || 0) + 90) % 360 })}
                            >
                              <RotateRightIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </>
                    )}
                  </>
                )}

                {selectedElement.kind === 'staticImage' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">Bild från DAM</Typography>
                    <TextField
                      size="small"
                      label="Sök bild i DAM"
                      placeholder="Ange titel eller filnamn..."
                      fullWidth
                      value={bannerImageSearch}
                      onChange={(e) => setBannerImageSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    {bannerImageOptions.length > 0 && bannerImageSearch.length >= 2 && (
                      <Paper sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                        {bannerImageOptions.slice(0, 10).map(option => (
                          <MenuItem 
                            key={option._id} 
                            onClick={() => {
                              const imageUrl = `/api/assets/${option._id}/download`;
                              updateElement(selectedElement.id, { imageUrl });
                              setBannerImageSearch('');
                              setBannerImageOptions([]);
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <img 
                                src={withImgToken(`/api/assets/${option._id}/preview/thumbnail`)}
                                alt="" 
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, backgroundColor: '#f5f5f5' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                              <Box>
                                <Typography variant="body2">{option.title || option.fileName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.fileName}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Paper>
                    )}

                    <Typography variant="caption" color="text.secondary">Eller ange URL direkt</Typography>
                    <TextField
                      size="small"
                      label="Bild-URL"
                      fullWidth
                      value={selectedElement.imageUrl || ''}
                      onChange={e => updateElement(selectedElement.id, { imageUrl: e.target.value })}
                      sx={{ mb: 2 }}
                      placeholder="https://... eller /api/assets/ID/download"
                      helperText={selectedElement.imageUrl ? 'Nuvarande bild' : 'Ingen bild vald'}
                    />

                    {selectedElement.imageUrl && (
                      <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <img 
                          src={withImgToken(
                            selectedElement.imageUrl.includes('/api/assets/') && selectedElement.imageUrl.includes('/download')
                              ? selectedElement.imageUrl.replace('/download', '/preview')
                              : selectedElement.imageUrl
                          )}
                          alt="Förhandsgranskning"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 100, 
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                          }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </Box>
                    )}
                  </>
                )}

                {selectedElement.kind === 'bulletList' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">Punktlista</Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Punkttyp</InputLabel>
                      <Select
                        value={selectedElement.style.bulletStyle || 'disc'}
                        label="Punkttyp"
                        onChange={e => updateElementStyle(selectedElement.id, { bulletStyle: e.target.value as 'disc' | 'circle' | 'square' | 'none' })}
                      >
                        {BULLET_STYLE_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Punkter (en per rad)"
                      fullWidth
                      multiline
                      rows={4}
                      value={selectedElement.content || ''}
                      onChange={e => updateElement(selectedElement.id, { content: e.target.value })}
                      sx={{ mb: 2 }}
                      placeholder="• Punkt ett&#10;• Punkt två&#10;• Punkt tre"
                      helperText="Skriv en punkt per rad"
                    />
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded key={`layer-${selectedElement.id}`} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Lagerordning</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                  <Tooltip title="Lägg längst bak">
                    <IconButton size="small" onClick={() => sendToBack(selectedElement.id)}>
                      <FlipToBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Lägg bakom">
                    <IconButton size="small" onClick={() => sendBackward(selectedElement.id)}>
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Lägg framför">
                    <IconButton size="small" onClick={() => bringForward(selectedElement.id)}>
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Lägg längst fram">
                    <IconButton size="small" onClick={() => bringToFront(selectedElement.id)}>
                      <FlipToFrontIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    z: {selectedElement.zIndex ?? 0}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Delete Element */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => deleteElement(selectedElement.id)}
              disabled={!!selectedElement.locked}
              fullWidth
            >
              Ta bort element
            </Button>
          </Box>
        ) : selectedSlot && activePanel === 'tools' ? (
          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              p: 1.25,
              borderRadius: 2,
              bgcolor: 'rgba(27, 57, 103, 0.05)',
            }}
          >
            {(() => {
              const slotIdx = (currentTemplate.productSlots || []).findIndex((s) => s.id === selectedSlot.id);
              const assigned = getAssignedMultiSlotAsset(multiPreviewAssets, slotIdx);
              const slotBlock = selectedSlot.blockTemplateId
                ? blockTemplates.find((block) => block._id === selectedSlot.blockTemplateId) || null
                : null;
              return (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Produktplats {slotIdx >= 0 ? slotIdx + 1 : ''}
                  </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Position i innehållsyta & storlek (mm)
                    </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="X"
                      type="number"
                      value={selectedSlot.xMm}
                      onChange={(e) => updateSlot(selectedSlot.id, { xMm: parseFloat(e.target.value) || 0 })}
                    />
                    <TextField
                      size="small"
                      label="Y"
                      type="number"
                      value={selectedSlot.yMm}
                      onChange={(e) => updateSlot(selectedSlot.id, { yMm: parseFloat(e.target.value) || 0 })}
                    />
                    <TextField
                      size="small"
                      label="Bredd"
                      type="number"
                      value={selectedSlot.wMm}
                      onChange={(e) => updateSlot(selectedSlot.id, { wMm: parseFloat(e.target.value) || 10 })}
                    />
                    <TextField
                      size="small"
                      label="Höjd"
                      type="number"
                      value={selectedSlot.hMm}
                      onChange={(e) => updateSlot(selectedSlot.id, { hMm: parseFloat(e.target.value) || 10 })}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">
                    Produktblock
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Block</InputLabel>
                    <Select
                      value={selectedSlot.blockTemplateId || ''}
                      label="Block"
                      onChange={(e) => updateSlot(selectedSlot.id, { blockTemplateId: String(e.target.value || '') || undefined })}
                    >
                      <MenuItem value="">
                        <em>Välj block…</em>
                      </MenuItem>
                      {blockTemplates.map((b) => (
                        <MenuItem key={b._id} value={b._id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {blocksLoading && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Laddar block…
                    </Typography>
                  )}
                  {!slotBlock && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Den här sloten saknar produktblock och kommer inte att renderas korrekt i exporten.
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">
                    Förhandsgranskning (slot)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {assigned ? (assigned.title || assigned.fileName) : 'Ingen produkt vald för preview'}
                  </Typography>
                  {!assigned && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Tomma slots i preview är nu verkligen tomma och återanvänder inte längre en primär produkt automatiskt.
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => updateSlot(selectedSlot.id, { blockTemplateId: undefined })}
                    fullWidth
                  >
                    Rensa block-koppling
                  </Button>
                </>
              );
            })()}
          </Box>
        ) : activePanel === 'templates' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              gap: 1,
            }}
          >
            <TextField
              size="small"
              fullWidth
              label={sidebarSearchLabel}
              placeholder={sidebarSearchPlaceholder}
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
            />
            <ToggleButtonGroup
              value={currentTemplate.type}
              exclusive
              onChange={(_, value) => value && setCurrentTemplate(prev => ({ ...prev, type: value }))}
              size="small"
              fullWidth
              sx={SEGMENTED_TOGGLE_SX}
            >
              <ToggleButton value="products">Produkter</ToggleButton>
              <ToggleButton value="wine">Vin</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ToggleButtonGroup
                value={templateViewMode}
                exclusive
                onChange={(_, value) => value && setTemplateViewMode(value)}
                size="small"
                sx={{
                  ...SEGMENTED_TOGGLE_SX,
                  flex: 1,
                  '& .MuiToggleButton-root': { ...SEGMENTED_TOGGLE_SX['& .MuiToggleButton-root'], flex: 1, px: 1.4 },
                }}
              >
                <ToggleButton value="grid">Rutnät</ToggleButton>
                <ToggleButton value="list">Lista</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Skapa ny mall">
                <IconButton
                  size="small"
                  onClick={handleNewTemplate}
                  aria-label="Skapa ny mall"
                  sx={{
                    flexShrink: 0,
                    width: 34,
                    height: 34,
                    borderRadius: 1.75,
                    color: FONTANA_COLORS.darkBlue,
                    border: '1px solid rgba(27, 57, 103, 0.18)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(27, 57, 103, 0.06)',
                      borderColor: 'rgba(27, 57, 103, 0.34)',
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {templateViewMode === 'list' ? (
            <List sx={{ py: 0 }}>
              {visibleTemplates.length === 0 ? (
                <Alert severity="info">{sidebarQuery ? 'Ingen mall matchar din filtrering.' : 'Det finns inga mallar för den här typen ännu.'}</Alert>
              ) : visibleTemplates.map(template => {
                const selected = template._id === currentTemplate._id;
                const cardTemplate = selected ? currentTemplate : template;
                const thumbnailKey = getTemplatePreviewKey(cardTemplate, previewAssetId, multiPreviewAssets);
                return (
                  <ListItemButton
                    key={template._id}
                    selected={selected}
                    sx={SIDEBAR_ITEM_SX}
                    onClick={() => {
                      clearPreview();
                      setCurrentTemplate(normalizeTemplate(template));
                      setSelectedElementId(null);
                      setSelectedSlotId(null);
                    }}
                  >
                    <Box sx={{ width: 56, flexShrink: 0, mr: 1.25 }}>
                      <RenderedTemplateThumbnail
                        template={cardTemplate}
                        selected={selected}
                        assetId={previewAssetId}
                        assetIds={multiPreviewAssetIds}
                        refreshKey={thumbnailKey}
                      />
                    </Box>
                    <ListItemText
                      primary={template.name || 'Namnlös'}
                      secondary={`${template.page.orientation === 'landscape' ? 'Liggande' : 'Stående'}${template.description ? ` • ${template.description}` : ''}`}
                    />
                  </ListItemButton>
                );
              })}
            </List>
            ) : visibleTemplates.length === 0 ? (
              <Alert severity="info">{sidebarQuery ? 'Ingen mall matchar din filtrering.' : 'Det finns inga mallar för den här typen ännu.'}</Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {visibleTemplates.map((template) => {
                  const selected = template._id === currentTemplate._id;
                  const cardTemplate = selected ? currentTemplate : template;
                  const thumbnailKey = getTemplatePreviewKey(cardTemplate, previewAssetId, multiPreviewAssets);
                  return (
                    <Box
                      key={template._id}
                      component="button"
                      type="button"
                      onClick={() => {
                        clearPreview();
                        setCurrentTemplate(normalizeTemplate(template));
                        setSelectedElementId(null);
                        setSelectedSlotId(null);
                      }}
                      sx={{
                        p: 1,
                        border: selected ? `1px solid ${FONTANA_COLORS.darkBlue}` : '1px solid rgba(27, 57, 103, 0.12)',
                        borderRadius: 2,
                        backgroundColor: selected ? 'rgba(27, 57, 103, 0.07)' : 'rgba(255,255,255,0.86)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        boxShadow: selected ? '0 10px 24px rgba(27, 57, 103, 0.14)' : '0 2px 8px rgba(15, 23, 42, 0.05)',
                        transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                        '&:hover': {
                          borderColor: 'rgba(27, 57, 103, 0.38)',
                          boxShadow: '0 10px 22px rgba(15, 23, 42, 0.12)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <RenderedTemplateThumbnail
                        template={cardTemplate}
                        selected={selected}
                        assetId={previewAssetId}
                        assetIds={multiPreviewAssetIds}
                        refreshKey={thumbnailKey}
                      />
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 1, color: FONTANA_COLORS.darkBlue }}>
                        {template.name || 'Namnlös'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {template.page.orientation === 'landscape' ? 'Liggande' : 'Stående'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ) : activePanel === 'layers' ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <TextField
              size="small"
              fullWidth
              label={sidebarSearchLabel}
              placeholder={sidebarSearchPlaceholder}
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Översta posten ligger längst fram. Låsta element kan inte flyttas förrän de låses upp.
            </Typography>
            {visibleLayers.length === 0 ? (
              <Alert severity="info">{sidebarQuery ? 'Ingen lagerpost matchar din filtrering.' : 'Inga element ännu. Lägg till objekt från verktygsrailen.'}</Alert>
            ) : (
              <List sx={{ py: 0 }}>
                {visibleLayers.map((element) => (
                  <ListItemButton
                    key={element.id}
                    selected={element.id === selectedElementId}
                    sx={{ ...SIDEBAR_ITEM_SX, alignItems: 'flex-start' }}
                    onClick={() => {
                      setSelectedElementId(element.id);
                      setSelectedSlotId(null);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}>{ELEMENT_ICONS[element.kind]}</ListItemIcon>
                    <ListItemText
                      primary={ELEMENT_KIND_LABELS[element.kind]}
                      secondary={`${Math.round(element.wMm)} x ${Math.round(element.hMm)} mm - z ${element.zIndex ?? 0}`}
                    />
                    <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5 }}>
                      <Tooltip title="Längst bak"><span><IconButton size="small" disabled={!!element.locked} onClick={(e) => { e.stopPropagation(); sendToBack(element.id); }}><FlipToBackIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Flytta ned"><span><IconButton size="small" disabled={!!element.locked} onClick={(e) => { e.stopPropagation(); sendBackward(element.id); }}><KeyboardArrowDownIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Flytta upp"><span><IconButton size="small" disabled={!!element.locked} onClick={(e) => { e.stopPropagation(); bringForward(element.id); }}><KeyboardArrowUpIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Längst fram"><span><IconButton size="small" disabled={!!element.locked} onClick={(e) => { e.stopPropagation(); bringToFront(element.id); }}><FlipToFrontIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title={element.locked ? 'Lås upp' : 'Lås'}><IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleElementLock(element.id); }}>{element.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}</IconButton></Tooltip>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        ) : activePanel === 'elements' ? (
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="subtitle2">Element</Typography>
            <Typography variant="body2" color="text.secondary">
              Lägg till objekt på sidan. När ett objekt är markerat visas dess egenskaper här.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {(Object.keys(ELEMENT_ICONS) as PdfElementKind[]).map((kind) => (
                <Button
                  key={kind}
                  variant="text"
                  disableRipple
                  onClick={() => addElement(kind)}
                  disabled={currentTemplate.templateMode === 'multi' && (kind === 'productImage' || kind === 'textField')}
                  sx={PANEL_TILE_SX}
                >
                  <Box className="panel-tile-icon">{ELEMENT_ICONS[kind]}</Box>
                  <Box component="span" sx={{ display: 'block' }}>{ELEMENT_KIND_LABELS[kind]}</Box>
                </Button>
              ))}
            </Box>
            {selectedElement && (
              <Alert severity="info">
                Markerat objekt är {ELEMENT_KIND_LABELS[selectedElement.kind]}. Byt till relevant panel eller klicka på Lager för översikt.
              </Alert>
            )}
          </Box>
        ) : activePanel === 'text' ? (
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2">Text</Typography>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('staticText')}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.staticText}</Box>
              <Box component="span">Lägg till textruta</Box>
            </Button>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('richText')}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.richText}</Box>
              <Box component="span">Lägg till rich text</Box>
            </Button>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('textField')}
              disabled={currentTemplate.templateMode === 'multi'}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.textField}</Box>
              <Box component="span">Lägg till datafält</Box>
            </Button>
            {currentTemplate.templateMode === 'multi' && (
              <Alert severity="info">Produktdata läggs i produktblock i multi-läge.</Alert>
            )}
          </Box>
        ) : activePanel === 'assets' ? (
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2">Bilder</Typography>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('productImage')}
              disabled={currentTemplate.templateMode === 'multi'}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.productImage}</Box>
              <Box component="span">Produktbild</Box>
            </Button>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('staticImage')}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.staticImage}</Box>
              <Box component="span">Statisk bild</Box>
            </Button>
            <Button
              variant="text"
              disableRipple
              onClick={() => addElement('logo')}
              sx={PANEL_LIST_BUTTON_SX}
            >
              <Box className="panel-list-icon">{ELEMENT_ICONS.logo}</Box>
              <Box component="span">Logotyp</Box>
            </Button>
            {currentTemplate.templateMode === 'multi' && (
              <Alert severity="info">Produktbilder för multi-mallar placeras i produktblock.</Alert>
            )}
          </Box>
        ) : activePanel === 'tools' ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {currentTemplate.templateMode !== 'multi' ? (
              <Alert severity="info">Produktplatser används i multi-läge.</Alert>
            ) : (
              <>
                <TextField
                  size="small"
                  fullWidth
                  label={sidebarSearchLabel}
                  placeholder={sidebarSearchPlaceholder}
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />
                {visibleSlots.length === 0 ? (
                  <Alert severity="info">{sidebarQuery ? 'Ingen produktplats matchar din filtrering.' : 'Inga produktplatser ännu.'}</Alert>
                ) : (
                  <List sx={{ py: 0 }}>
                    {visibleSlots.map(({ slot, index, block, assigned }) => (
                      <ListItemButton
                        key={slot.id}
                        selected={slot.id === selectedSlotId}
                        sx={SIDEBAR_ITEM_SX}
                        onClick={() => {
                          setSelectedSlotId(slot.id);
                          setSelectedElementId(null);
                        }}
                      >
                        <ListItemText
                          primary={`Produktplats ${index + 1}`}
                          secondary={`${Math.round(slot.wMm)} x ${Math.round(slot.hMm)} mm - ${block?.name || 'Inget block'}${assigned ? ` - ${assigned.title || assigned.fileName}` : ''}`}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="subtitle2">Preview</Typography>
            {previewError && (
              <Alert severity="error">{previewError}</Alert>
            )}
            <Alert severity={previewIsStale ? 'warning' : previewUrl ? 'success' : 'info'}>
              {previewIsStale ? 'Previewn behöver uppdateras.' : previewUrl ? 'Previewn är aktuell.' : 'Välj produkt och generera preview.'}
            </Alert>
            <Autocomplete
              size="small"
              options={assetOptions}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option.title || option.fileName || ''}
              inputValue={assetSearch}
              onInputChange={(_, value) => setAssetSearch(value)}
              onChange={(_, asset) => {
                if (!asset) return;
                if (currentTemplate.templateMode === 'multi') {
                  addMultiPreviewAsset(asset._id);
                } else {
                  setPreviewAssetId(asset._id);
                }
                setAssetSearch('');
              }}
              renderInput={(params) => (
                <TextField {...params} label={currentTemplate.templateMode === 'multi' ? 'Lägg till produkt' : 'Sök produkt'} />
              )}
            />
            <FormControlLabel
              control={<Switch size="small" checked={autoPreviewEnabled} onChange={(e) => setAutoPreviewEnabled(e.target.checked)} />}
              label="Auto-preview"
            />
            <Button
              variant={previewIsStale || !previewUrl ? 'contained' : 'outlined'}
              startIcon={previewLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={generatePreview}
              disabled={!hasPreviewSource || previewLoading}
              fullWidth
              sx={{
                ...PANEL_ACTION_BUTTON_SX,
                ...(previewIsStale || !previewUrl
                  ? {
                      backgroundColor: FONTANA_COLORS.darkBlue,
                      borderColor: FONTANA_COLORS.darkBlue,
                      color: '#fff',
                      '& .MuiButton-startIcon': { color: '#fff' },
                      '&:hover': {
                        backgroundColor: '#14294a',
                        borderColor: '#14294a',
                        boxShadow: '0 8px 18px rgba(27, 57, 103, 0.18)',
                      },
                    }
                  : {}),
              }}
            >
              {previewLoading ? 'Genererar...' : previewIsStale ? 'Uppdatera preview' : 'Generera preview'}
            </Button>
            {previewUrl && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<FullscreenIcon />} onClick={() => setPreviewDialogOpen(true)} sx={{ ...PANEL_ACTION_BUTTON_SX, flex: 1 }}>Fullskärm</Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadPreview} sx={{ ...PANEL_ACTION_BUTTON_SX, flex: 1 }}>Ladda ner</Button>
              </Box>
            )}
            {previewAsset && currentTemplate.templateMode !== 'multi' && (
              <Chip label={previewAsset.title || previewAsset.fileName} onDelete={() => { setPreviewAssetId(''); setPreviewAsset(null); clearPreview(); }} size="small" color="primary" variant="outlined" />
            )}
            {currentTemplate.templateMode === 'multi' && multiPreviewAssets.map((asset, index) => (
              <Chip key={asset._id} label={`${index + 1}. ${asset.title || asset.fileName}`} size="small" variant="outlined" onDelete={() => removeMultiPreviewAsset(asset._id)} />
            ))}
          </Box>
        )}
          </Box>
        </Box>
      </Paper>
      )}

      <PdfSettingsDialogs
        dialog={settingsDialog}
        currentTemplate={currentTemplate}
        contentWidthMm={contentWidthMm}
        contentHeightMm={contentHeightMm}
        previewIsStale={previewIsStale}
        previewUrl={previewUrl}
        validationErrors={templateValidationErrors}
        validationWarnings={templateValidationWarnings}
        onClose={() => setSettingsDialog(null)}
        onChangeTemplate={setCurrentTemplate}
      />
      
      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(prev => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
      
      {/* Fullscreen Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Förhandsgranskning - {currentTemplate.name || 'Namnlös mall'}
          </Typography>
          <IconButton onClick={() => setPreviewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                flex: 1,
              }}
              title="PDF Preview Fullscreen"
            />
          ) : (
            <Box 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
              }}
            >
              <Typography color="text.secondary">
                Ingen förhandsgranskning tillgänglig. Lägg till en produkt och klicka "Generera".
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PdfTemplatesPage;
